// src/api/hooks.js
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import apiClient from "./client";
import socketService from "../services/socketService";
import {
  addProject,
  createEmployee,
  createTask,
  createTaskFromBoard,
  getTaskById,
  updatedProfile,
  extendEmployeeProbation,
  closeEmployeeProbation,
  updateEmployeeProbationDates,
  updateProject,
  updateTaskById,
  moveTaskToProject,
  moveTaskToMonth,
  updateTaskOrder,
  deleteProject,
  pauseProject,
  resumeProject,
  createSubTask,
  getSubTasksByParentTask,
  getSubTaskById,
  updateSubTaskById,
  createReworkEvent,
  getReworkCandidates,
  deleteSubTask,
  addSubTaskAttachments,
  removeSubTaskAttachment,
  getUserNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  getUnreadNotificationCount,
  uploadSingleFile,
  getTasksOnReview,
  getTasksOnPublish,
  getClientReviewTasks,
  markSentToClient,
  getUnscheduledTasks,
  scheduleSubTask,
  // Sticky Notes imports
  createStickyNote,
  getUserStickyNotes,
  getStickyNoteById,
  updateStickyNote,
  deleteStickyNote,
  updateStickyNotePositions,
  toggleArchiveStickyNote,
  setStickyNoteReminder,
  clearStickyNoteReminder,
  getArchivedStickyNotes,
  getStickyNotesWithReminders,
  bulkDeleteStickyNotes,
  // Attendance imports
  clockIn,
  clockOut,
  startBreak,
  endBreak,
  getCurrentAttendanceStatus,
  getEmployeeAttendanceHistory,
  getDailyAttendanceReport,
  getAttendanceSummary,
  getAttendanceAnalytics,
  updateAttendanceRecord,
  approveAttendance,
  deleteAttendanceRecord,
  // Task Flow imports
  getTaskFlows,
  resolveTaskFlow,
  createTaskFlow,
  updateTaskFlow,
  deleteTaskFlow,
  restoreTaskFlow,
  permanentDeleteTaskFlow,
  deleteAllCompanyTasks,
  deleteAllCompanyProjects,
  deleteAllCompanyEmployees,
  // Project Field imports
  getProjectFields,
  createProjectField,
  updateProjectField,
  deleteProjectField,
  reorderProjectFields,
  submitSubTaskPendingReason,
  // Holiday imports
  getHolidays,
  createHoliday,
  bulkCreateHolidays,
  updateHoliday,
  deleteHoliday,
  bulkDeleteHolidays,
  // Work Schedule imports
  getWorkSchedule,
  saveWorkSchedule,
  getLeavePolicy,
  saveLeavePolicy,
  getCompanyById,
  updateCompany,
  resetCompanyPerformance,
  resetEmployeePerformance,
  updatePerformanceLedgerEntry,
  deletePerformanceLedgerEntry,
} from "./service";
import { 
  getScheduledPosts, 
  deleteScheduledPost,
  getPostInsights,
  getPostComments,
  replyToComment,
  likeComment,
  getAudienceDemographics,
  getProfileInteractions,
  getActiveStories,
  getMentionsAndTags,
  searchHashtag
} from "./social";
import { getUnreadCount } from "./chatService";
import { format } from "date-fns";
import { useAuth } from "../hooks/useAuth";

// Task Flow hooks

export const useGetTaskFlows = (companyId) => {
  return useQuery({
    queryKey: ["taskFlows", companyId],
    queryFn: () => getTaskFlows(companyId),
    enabled: !!companyId,
    select: (data) => data?.taskFlows || [],
  });
};

// Stage-by-stage owners of a flow on a project. Re-runs when the flow or project changes.
export const useResolveTaskFlow = (companyId, taskFlowId, projectId, preferredUserIds = []) => {
  return useQuery({
    queryKey: ["resolvedTaskFlow", companyId, taskFlowId, projectId || null],
    queryFn: () =>
      resolveTaskFlow(companyId, taskFlowId, { projectId: projectId || null, preferredUserIds }),
    enabled: !!companyId && !!taskFlowId,
    select: (data) => data?.stages || [],
    // A fresh pick every time: "least busy" changes as work is assigned
    staleTime: 0,
    gcTime: 60 * 1000,
  });
};

export const useGetDepartmentConflicts = (companyId, departmentId) => {
  return useQuery({
    queryKey: ["departmentConflicts", companyId, departmentId],
    queryFn: () => apiClient.get(`/companies/${companyId}/departments/${departmentId}/conflicts`).then(res => res.data),
    enabled: !!companyId && !!departmentId,
  });
};

export const useCreateTaskFlow = (companyId, onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (taskFlowData) => createTaskFlow(companyId, taskFlowData),
    onSuccess: (...args) => {
      queryClient.invalidateQueries(["taskFlows", companyId]);
      if (onSuccess) onSuccess(...args);
    },
  });
};

export const useUpdateTaskFlow = (companyId, onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ taskFlowId, taskFlowData }) =>
      updateTaskFlow(companyId, taskFlowId, taskFlowData),
    onSuccess: (...args) => {
      queryClient.invalidateQueries(["taskFlows", companyId]);
      if (onSuccess) onSuccess(...args);
    },
  });
};

export const useDeleteTaskFlow = (companyId, onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (taskFlowId) => deleteTaskFlow(companyId, taskFlowId),
    onSuccess: (...args) => {
      queryClient.invalidateQueries(["taskFlows", companyId]);
      if (onSuccess) onSuccess(...args);
    },
  });
};

export const useRestoreTaskFlow = (companyId, onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (taskFlowId) => restoreTaskFlow(companyId, taskFlowId),
    onSuccess: (...args) => {
      queryClient.invalidateQueries(["taskFlows", companyId]);
      if (onSuccess) onSuccess(...args);
    },
  });
};

export const usePermanentDeleteTaskFlow = (companyId, onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (taskFlowId) => permanentDeleteTaskFlow(companyId, taskFlowId),
    onSuccess: (...args) => {
      queryClient.invalidateQueries(["taskFlows", companyId]);
      if (onSuccess) onSuccess(...args);
    },
  });
};

// Customers
export const useCustomers = () =>
  useQuery({
    queryKey: ["customers"],
    queryFn: () => apiClient.get("/customers").then((res) => res.data),
  });

export const useCompanyProjects = (companyId, limit = 0, monthKey = null, options = {}) => {
  const view = options.view || null;
  return useQuery({
    queryKey: ["companyProjects", companyId, limit, monthKey, view],
    queryFn: () => {
      const params = new URLSearchParams();
      if (limit) params.append("limit", limit.toString());
      if (monthKey) params.append("monthKey", monthKey);
      if (view) params.append("view", view);
      params.append("active", "true");

      const queryString = params.toString();
      return apiClient
        .get(
          `/projects/company/${companyId}${queryString ? `?${queryString}` : ""
          }`
        )
        .then((res) => res.data?.projects);
    },
    enabled: options.enabled !== undefined ? !!companyId && options.enabled : !!companyId,
    staleTime: 5 * 60 * 1000, // 5 minutes fresh
    gcTime: 15 * 60 * 1000,  // 15 minutes cache
  });
};

export const useCompanyActiveProjects = () => {
  return useQuery({
    queryKey: ["companyActiveProjects"],
    queryFn: () =>
      apiClient
        .get("/projects/company/active")
        .then((res) => res.data?.projects),
    staleTime: 0,
    cacheTime: 0,
    refetchOnWindowFocus: true,
  });
};

export const useCompanyWorkDetailsByMonth = (companyId, monthKey = null) => {
  return useQuery({
    queryKey: ["companyWorkDetails", companyId, monthKey],
    queryFn: () => {
      const url = monthKey
        ? `/projects/company/${companyId}/work-details?month=${monthKey}`
        : `/projects/company/${companyId}/work-details`;
      return apiClient.get(url).then((res) => res.data?.data);
    },
    enabled: !!companyId,
    // Always fetch fresh data when visiting
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    staleTime: 0, // Consider data stale immediately to force refetch
  });
};

export const useProjectDetails = (projectId, monthKeyOrOptions = null) => {
  const isOptions =
    monthKeyOrOptions &&
    typeof monthKeyOrOptions === "object" &&
    !Array.isArray(monthKeyOrOptions);
  const monthKey = isOptions
    ? typeof monthKeyOrOptions.monthKey === "string"
      ? monthKeyOrOptions.monthKey
      : null
    : monthKeyOrOptions;
  const view = isOptions ? monthKeyOrOptions.view || null : null;
  const enabledFromOptions = isOptions ? monthKeyOrOptions.enabled : undefined;

  return useQuery({
    queryKey: ["projectDetails", projectId, monthKey, view],
    queryFn: () => {
      const params = new URLSearchParams();
      if (monthKey) params.append("monthKey", monthKey);
      if (view) params.append("view", view);
      const queryString = params.toString();
      const url = queryString
        ? `/projects/${projectId}?${queryString}`
        : `/projects/${projectId}`;
      return apiClient.get(url).then((res) => res.data?.project || null);
    },
    enabled:
      enabledFromOptions !== undefined
        ? !!projectId && enabledFromOptions
        : !!projectId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000,
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey?.[1] === projectId ? previousData : undefined,
  });
};

// New hook for fetching project tasks separately
export const useProjectTasks = (projectId, monthKey = null, options = {}) => {
  const view = options.view || null;
  return useQuery({
    queryKey: ["projectTasks", projectId, monthKey, view],
    queryFn: () => {
      const params = new URLSearchParams();
      if (monthKey) params.append("monthKey", monthKey);
      if (view) params.append("view", view);
      const queryString = params.toString();
      const url = queryString
        ? `/projects/${projectId}/tasks?${queryString}`
        : `/projects/${projectId}/tasks`;
      return apiClient
        .get(url)
        .then((res) => [...(res.data?.tasks || []), ...(res.data?.subTasks || [])]);
    },
    enabled:
      options.enabled !== undefined ? !!projectId && options.enabled : !!projectId,
    staleTime: 1000 * 60 * 1, // 1 minute (Tasks change more frequently)
    gcTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey?.[1] === projectId ? previousData : undefined,
  });
};

//empoyee
export const useEmpoyees = (page = null, filters = null, search = "", view = null, archived = false) => {
  return useQuery({
    queryKey: ["employees", page, filters, search, view, archived],
    queryFn: async () => {
      const params = new URLSearchParams();

      if (page) {
        params.append("page", page.toString());
        params.append("limit", "10");
      }

      if (filters) {
        params.append(
          "filters",
          typeof filters === "string" ? filters : JSON.stringify(filters)
        );
      }

      if (search) {
        params.append("search", search);
      }

      if (view) {
        params.append("view", view);
      }

      if (archived) {
        params.append("archived", "true");
      }

      const response = await apiClient.get(`/employee?${params.toString()}`);
      return response.data;
    },
    keepPreviousData: true,
  });
};

// Hook to get all employees (for tasks without projects)
export const useGetAllEmployees = (enabled = true, options = {}) => {
  const view = options.view || null;
  return useQuery({
    queryKey: ["allEmployees", view],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: "1",
        limit: "1000", // Large limit to get all employees
      });
      if (view) params.append("view", view);

      const response = await apiClient.get(`/employee?${params.toString()}`);
      return response.data;
    },
    enabled,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000,   // 30 minutes cache
  });
};
// task

export const useCreateTask = (handleClose, projectId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["createTask"],
    mutationFn: (taskData) => createTask(taskData, projectId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["projectTasks", projectId],
      });
      // Creating a task uses up monthly quota, which lives on the project.
      queryClient.invalidateQueries({ queryKey: ["projectDetails"] });
      window.dispatchEvent(new Event('taskCreated'));
      handleClose(data);
      // toast.success('Target set successfully!');
    },
  });
};

export const useCreateTaskFromBoard = (handleClose) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationKey: ["createTaskFromBoard"],
    mutationFn: (taskData) => createTaskFromBoard(taskData),
    onSuccess: (data) => {
      // Invalidate relevant queries based on user role
      if (user?.role === "company-admin") {
        queryClient.invalidateQueries(["companyTasks", user?.company]);
      } else {
        queryClient.invalidateQueries(["employeeTasks", user?._id]);
      }
      queryClient.invalidateQueries({ queryKey: ["boardColumn"] });
      queryClient.invalidateQueries({ queryKey: ["boardMeta"] });
      window.dispatchEvent(new Event('taskCreated'));
      handleClose(data);
      // toast.success('Task created successfully!');
    },
  });
};

export const useUpdateProfile = (handleSuccess, employeeId = null) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["updateProfile", employeeId],
    mutationFn: (updatedData) => updatedProfile(updatedData, employeeId),
    onSuccess: (data, variables) => {
      // Invalidate all employee-related queries
      queryClient.invalidateQueries(["employee"]);
      queryClient.invalidateQueries(["employees"]);

      // If we have the updated employee data, update the specific employee cache
      if (data?.employee?._id) {
        queryClient.setQueryData(["employee", data.employee._id], data);
        // Also invalidate the specific employee query to ensure fresh data
        queryClient.invalidateQueries(["employee", data.employee._id]);
      }

      // Invalidate auth-related queries that might contain user profile data
      queryClient.invalidateQueries(["auth"]);
      queryClient.invalidateQueries(["user"]);
      queryClient.invalidateQueries(["myVacations"]);
      queryClient.invalidateQueries(["employeeVacations"]);
      queryClient.invalidateQueries(["companyVacations"]);
      queryClient.invalidateQueries(["vacationsCalendar"]);
      queryClient.invalidateQueries(["allEmployees"]);

      // Call the success handler if provided
      if (handleSuccess) {
        handleSuccess(data);
      }
    },
  });
};

export const useExtendEmployeeProbation = (employeeId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["extendProbation", employeeId],
    mutationFn: (payload) => extendEmployeeProbation(employeeId, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries(["employee"]);
      queryClient.invalidateQueries(["employees"]);
      queryClient.invalidateQueries(["allEmployees"]);
      queryClient.invalidateQueries(["myVacations"]);
      queryClient.invalidateQueries(["employeeVacations"]);
      if (data?.employee?._id) {
        queryClient.setQueryData(["employee", data.employee._id], data);
        queryClient.invalidateQueries(["employee", data.employee._id]);
      }
    },
  });
};

export const useCloseEmployeeProbation = (employeeId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["closeProbation", employeeId],
    mutationFn: (payload) => closeEmployeeProbation(employeeId, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries(["employee"]);
      queryClient.invalidateQueries(["employees"]);
      queryClient.invalidateQueries(["allEmployees"]);
      queryClient.invalidateQueries(["myVacations"]);
      queryClient.invalidateQueries(["employeeVacations"]);
      if (data?.employee?._id) {
        queryClient.setQueryData(["employee", data.employee._id], data);
        queryClient.invalidateQueries(["employee", data.employee._id]);
      }
    },
  });
};

export const useUpdateEmployeeProbationDates = (employeeId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["updateProbationDates", employeeId],
    mutationFn: (payload) => updateEmployeeProbationDates(employeeId, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries(["employee"]);
      queryClient.invalidateQueries(["employees"]);
      queryClient.invalidateQueries(["allEmployees"]);
      queryClient.invalidateQueries(["myVacations"]);
      queryClient.invalidateQueries(["employeeVacations"]);
      if (data?.employee?._id) {
        queryClient.setQueryData(["employee", data.employee._id], data);
        queryClient.invalidateQueries(["employee", data.employee._id]);
      }
    },
  });
};

// New hook for uploading profile images
export const useUploadProfileImage = (onSuccess, onError) => {
  return useMutation({
    mutationFn: (file) => uploadSingleFile(file),
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      if (onError) onError(error);
    },
  });
};

export const useGetTaskById = (taskId) => {
  return useQuery({
    queryKey: ["getTaskById", taskId],
    queryFn: () => getTaskById(taskId),
    select: (data) => data?.task,
  });
};

export const useUpdateTaskById = (taskId, handleClose) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["updatedTaskById", taskId],
    mutationFn: (updatedData) => updateTaskById(taskId, updatedData),
    onSuccess: () => {
      queryClient.invalidateQueries(["getTaskById", taskId]);
      // Changing a task's work type moves quota between work types.
      queryClient.invalidateQueries({ queryKey: ["projectDetails"] });
      // Saving can reorder the subtasks.
      queryClient.invalidateQueries({ queryKey: ["subTasksByParentTask", taskId] });
      // A shoot's covered tasks can change here
      queryClient.invalidateQueries({ queryKey: ["shootCandidates"] });
      handleClose();
    },
  });
};

export const useMoveTask =(taskId, onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["moveTask", taskId],
    mutationFn: (moveData) => moveTaskToProject(taskId, moveData),
    onSuccess: (data) => {
      queryClient.invalidateQueries(["getTaskById", taskId]);
      queryClient.invalidateQueries(["subTasksByParentTask", taskId]);
      queryClient.invalidateQueries(["companyProjects"]);
      queryClient.invalidateQueries(["tasksByProject"]);
      queryClient.invalidateQueries(["projectDetails"]);
      queryClient.invalidateQueries(["projectTasks"]);
      queryClient.invalidateQueries(["companyWorkDetails"]);
      queryClient.invalidateQueries(["companyStats"]);
      queryClient.invalidateQueries(["sidebarTaskCounts"]);
      queryClient.invalidateQueries({ queryKey: ["boardColumn"] });
      queryClient.invalidateQueries({ queryKey: ["boardMeta"] });
      if (onSuccess) onSuccess(data);
    },
  });
};

export const useMoveTaskToMonth = (taskId, onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["moveTaskToMonth", taskId],
    mutationFn: (moveData) => moveTaskToMonth(taskId, moveData),
    onSuccess: (data) => {
      queryClient.invalidateQueries(["getTaskById", taskId]);
      queryClient.invalidateQueries(["subTasksByParentTask", taskId]);
      queryClient.invalidateQueries(["companyProjects"]);
      queryClient.invalidateQueries(["tasksByProject"]);
      queryClient.invalidateQueries(["projectDetails"]);
      queryClient.invalidateQueries(["projectTasks"]);
      queryClient.invalidateQueries(["companyWorkDetails"]);
      queryClient.invalidateQueries(["companyStats"]);
      queryClient.invalidateQueries(["sidebarTaskCounts"]);
      queryClient.invalidateQueries({ queryKey: ["boardColumn"] });
      queryClient.invalidateQueries({ queryKey: ["boardMeta"] });
      if (onSuccess) onSuccess(data);
    },
  });
};

export const useAddEmployee = (handleClose) => {
  return useMutation({
    mutationKey: ["addEmployee"],
    mutationFn: (data) => createEmployee(data),
  });
};

/////////////  PROJECT SECTION ⚠️⚠️⚠️⚠️⚠️ ////////////////////

// Add PROJECT
export const useAddProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["addProject"],
    mutationFn: (projectDetails) => addProject(projectDetails),
    onSuccess: () => {
      queryClient.invalidateQueries(["companyProjects"]);
    },
  });
};

export const useUpdateProject = (projectId, handleSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["updateProject"],
    mutationFn: (updatedData) => updateProject(updatedData, projectId),
    onSuccess: () => {
      handleSuccess();
      queryClient.invalidateQueries(["projectDetails", projectId]);
    },
  });
};

// Time Log Hooks
export const useGetTaskTimeLogs = (taskId) => {
  return useQuery({
    queryKey: ["taskTimeLogs", taskId],
    queryFn: () =>
      apiClient.get(`/time-logs/task/${taskId}`).then((res) => res.data),
    enabled: !!taskId,
  });
};

export const useCreateTimeLog = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (timeLogData) =>
      apiClient.post("/time-logs", timeLogData).then((res) => res.data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(["taskTimeLogs", variables.taskId]);
    },
  });
};

export const useUpdateTaskOrder = (projectId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, newOrder }) => updateTaskOrder(taskId, newOrder),
    onSuccess: () => {
      // Invalidate project details to refresh the task list
      queryClient.invalidateQueries(["projectDetails", projectId]);
    },
  });
};

export const useGetEmployeeProjects = (employeeId, monthKey = null, options = {}) => {
  const view = options.view || null;
  return useQuery({
    queryKey: ["employeeProjects", employeeId, monthKey, view],
    queryFn: () => {
      const params = new URLSearchParams();
      if (monthKey) params.append("monthKey", monthKey);
      if (view) params.append("view", view);
      params.append("active", "true");

      const queryString = params.toString();
      return apiClient
        .get(`/projects/employee/${employeeId}?${queryString}`)
        .then((res) => res.data)
        .catch((error) => {
          // Fallback data if endpoint doesn't exist or has errors
          console.warn("Employee projects endpoint not available:", error);
          return {
            projects: [
              // Sample project data for demonstration
              {
                _id: "sample-project-1",
                name: "Sample Employee Project",
                description: "A sample project assigned to the employee",
                status: "active",
                progress: 65,
                dueDate: new Date(
                  Date.now() + 7 * 24 * 60 * 60 * 1000
                ).toISOString(), // 7 days from now
                teams: [],
                tasks: [],
              },
              {
                _id: "sample-project-2",
                name: "Mobile App Development",
                description: "Developing a mobile application for the company",
                status: "active",
                progress: 40,
                dueDate: new Date(
                  Date.now() + 14 * 24 * 60 * 60 * 1000
                ).toISOString(), // 14 days from now
                teams: [],
                tasks: [],
              },
              {
                _id: "sample-project-3",
                name: "Website Redesign",
                description: "Complete redesign of the company website",
                status: "active",
                progress: 80,
                dueDate: new Date(
                  Date.now() + 3 * 24 * 60 * 60 * 1000
                ).toISOString(), // 3 days from now
                teams: [],
                tasks: [],
              },
            ],
          };
        });
    },
    enabled: !!employeeId, // Only run if employeeId exists and is not null
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

export const useGetEmployee = (employeeId) => {
  return useQuery({
    queryKey: ["employee", employeeId],
    queryFn: () =>
      apiClient.get(`/employee/${employeeId}`).then((res) => res.data),
    enabled: !!employeeId,
  });
};

export const useGetEmployeeTeams = (employeeId, projectId) => {
  return useQuery({
    queryKey: ["employeeTeams", employeeId, projectId],
    queryFn: () =>
      apiClient
        .get(`/teams/employee/${employeeId}`, {
          params: { projectId },
        })
        .then((res) => res.data)
        .catch((error) => {
          console.warn("Employee teams endpoint not available:", error);
          return { teams: [] };
        }),
    enabled: !!employeeId && !!projectId,
  });
};

export const useGetEmployeeStatistics = (employeeId, taskMonth) => {
  return useQuery({
    queryKey: ["employeeStatistics", employeeId, taskMonth],
    queryFn: () => {
      const params = new URLSearchParams();
      if (taskMonth) params.append("taskMonth", taskMonth);

      return apiClient
        .get(`/dashboard/employee-statistics/${employeeId}?${params.toString()}`)
        .then((res) => res.data);
    },
    enabled: !!employeeId && !!taskMonth,
  });
};

export const useGetProjectsDueThisMonth = (date = new Date()) => {
  return useQuery({
    queryKey: ["projectsDueThisMonth", format(date, "yyyy-MM")],
    queryFn: () =>
      apiClient
        .get(
          `/projects/due-this-month?date=${format(
            date,
            "yyyy-MM-dd"
          )}&active=true`
        )
        .then((res) => res.data),
  });
};

export const useGetTasksDueThisMonth = (date = new Date()) => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1; // JavaScript months are 0-indexed

  return useQuery({
    queryKey: ["tasksDueThisMonth", format(date, "yyyy-MM")],
    queryFn: () =>
      apiClient.get(`/tasks/month/${year}/${month}`).then((res) => res.data),
  });
};

export const useDeleteProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["deleteProject"],
    mutationFn: (projectId) =>
      apiClient.delete(`/projects/${projectId}`).then((res) => res.data),
    onSuccess: () => {
      // Invalidate all project-related queries
      queryClient.invalidateQueries(["projectDetails"]);
      queryClient.invalidateQueries(["companyProjects"]);
    },
  });
};

export const usePauseProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["pauseProject"],
    mutationFn: (projectId) => pauseProject(projectId),
    onSuccess: () => {
      // Invalidate all project-related queries
      queryClient.invalidateQueries(["projectDetails"]);
      queryClient.invalidateQueries(["companyProjects"]);
    },
  });
};

export const useResumeProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["resumeProject"],
    mutationFn: (projectId) => resumeProject(projectId),
    onSuccess: () => {
      // Invalidate all project-related queries
      queryClient.invalidateQueries(["projectDetails"]);
      queryClient.invalidateQueries(["companyProjects"]);
    },
  });
};

// Position Management Hooks
export const useGetPositions = (companyId) => {
  return useQuery({
    queryKey: ["positions", companyId],
    queryFn: () =>
      apiClient
        .get(`/companies/${companyId}/positions`)
        .then((res) => res.data),
    enabled: !!companyId,
  });
};

export const useGetPosition = (companyId, positionId) => {
  return useQuery({
    queryKey: ["position", companyId, positionId],
    queryFn: () =>
      apiClient
        .get(`/companies/${companyId}/positions/${positionId}`)
        .then((res) => res.data),
    enabled: !!companyId && !!positionId,
  });
};

export const useCreatePosition = (companyId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["createPosition"],
    mutationFn: (positionData) =>
      apiClient
        .post(`/companies/${companyId}/positions`, positionData)
        .then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries(["positions", companyId]);
    },
  });
};

export const useUpdatePosition = (companyId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["updatePosition"],
    mutationFn: ({ id, ...positionData }) =>
      apiClient
        .put(`/companies/${companyId}/positions/${id}`, positionData)
        .then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries(["positions", companyId]);
    },
  });
};

export const useDeletePosition = (companyId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["deletePosition"],
    mutationFn: (id) =>
      apiClient
        .delete(`/companies/${companyId}/positions/${id}`)
        .then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries(["positions", companyId]);
    },
  });
};

export const useRestorePosition = (companyId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["restorePosition"],
    mutationFn: (id) =>
      apiClient
        .patch(`/companies/${companyId}/positions/${id}/restore`)
        .then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries(["positions", companyId]);
    },
  });
};

// Get position permissions
export const useGetPermissions = (companyId, positionId) => {
  return useQuery({
    queryKey: ["permissions", companyId, positionId],
    queryFn: () =>
      apiClient
        .get(`/companies/${companyId}/positions/${positionId}/permissions`)
        .then((res) => res.data),
    enabled: !!companyId && !!positionId,
  });
};

// Update position permissions
export const useUpdatePermissions = (companyId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["updatePermissions"],
    mutationFn: ({ positionId, permissions, allowedRoutes, dailyTaskLimit }) =>
      apiClient
        .put(`/companies/${companyId}/positions/${positionId}/permissions`, {
          permissions,
          allowedRoutes,
          dailyTaskLimit,
        })
        .then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries(["positions", companyId]);
      queryClient.invalidateQueries(["permissions"]);
    },
  });
};

export const useDeleteTask = (projectId, onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["deleteTask"],
    mutationFn: (taskId) =>
      apiClient.delete(`/tasks/${taskId}`).then((res) => res.data),
    onSuccess: () => {
      // Invalidate both task and project queries to refresh the data
      queryClient.invalidateQueries(["projectDetails"]);
      if (onSuccess) onSuccess();
    },
  });
};

export const useGetEmployeeBirthdays = (date = new Date()) => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1; // JavaScript months are 0-indexed

  return useQuery({
    queryKey: ["employeeBirthdays", format(date, "yyyy-MM")],
    queryFn: () =>
      apiClient
        .get(`/employee/birthdays/${year}/${month}`)
        .then((res) => res.data),
  });
};

// Vacation hooks
export const useCreateVacationRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vacationData) =>
      apiClient.post("/vacations", vacationData).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries(["myVacations"]);
      queryClient.invalidateQueries(["vacationsCalendar"]);
      queryClient.invalidateQueries(["companyVacations"]);
    },
  });
};

export const useGetMyVacations = (enabled = true) => {
  return useQuery({
    queryKey: ["myVacations"],
    queryFn: () =>
      apiClient.get("/vacations/my-vacations").then((res) => res.data),
    enabled,
    staleTime: 15 * 1000,
  });
};

export const useGetEmployeeVacations = (employeeId, month, year, options = {}) => {
  const includePending = !!options.includePending;
  const queryParams = new URLSearchParams();
  if (month) queryParams.append("month", month);
  if (year) queryParams.append("year", year);
  if (includePending) queryParams.append("includePending", "true");

  return useQuery({
    queryKey: ["employeeVacations", employeeId, month, year, includePending],
    queryFn: () =>
      apiClient
        .get(`/vacations/employee/${employeeId}?${queryParams.toString()}`)
        .then((res) => res.data),
    enabled: !!employeeId,
  });
};

export const useGetCompanyVacations = (month, year) => {
  const queryParams = new URLSearchParams();
  if (month) queryParams.append("month", month);
  if (year) queryParams.append("year", year);

  return useQuery({
    queryKey: ["companyVacations", month, year],
    queryFn: () =>
      apiClient
        .get(`/vacations/company?${queryParams.toString()}`)
        .then((res) => res.data),
  });
};

export const useGetVacationsCalendar = (month, year) => {
  return useQuery({
    queryKey: ["vacationsCalendar", month, year],
    queryFn: () =>
      apiClient
        .get(`/vacations/calendar?month=${month}&year=${year}`)
        .then((res) => res.data),
    enabled: !!month && !!year,
  });
};

export const useUpdateVacationStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ vacationId, status, notes }) =>
      apiClient
        .patch(`/vacations/${vacationId}/status`, { status, notes })
        .then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries(["myVacations"]);
      queryClient.invalidateQueries(["vacationsCalendar"]);
      queryClient.invalidateQueries(["companyVacations"]);
    },
  });
};

export const useUpdateVacationRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ vacationId, data }) =>
      apiClient.put(`/vacations/${vacationId}`, data).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries(["myVacations"]);
      queryClient.invalidateQueries(["vacationsCalendar"]);
      queryClient.invalidateQueries(["companyVacations"]);
    },
  });
};

// Recent Activities Hook
export const useGetRecentActivities = (
  limit = 10,
  type = "all",
  employeeIds = null,
  projectIds = null
) => {
  return useQuery({
    queryKey: ["recentActivities", limit, type, employeeIds, projectIds],
    queryFn: () => {
      const params = new URLSearchParams({
        limit: limit.toString(),
      });

      if (type && type !== "all") {
        params.append("type", type);
      }

      if (employeeIds && Array.isArray(employeeIds) && employeeIds.length > 0) {
        params.append("employeeIds", employeeIds.join(","));
      }

      if (projectIds && Array.isArray(projectIds) && projectIds.length > 0) {
        params.append("projectIds", projectIds.join(","));
      }

      return apiClient
        .get(`/time-logs/recent-activities?${params.toString()}`)
        .then((res) => res.data);
    },
    staleTime: 1000 * 60 * 1, // 1 minute (reduced for more frequent updates)
    refetchInterval: 1000 * 60 * 3, // Refetch every 3 minutes (more frequent)
    refetchOnWindowFocus: true, // Refetch when window gains focus (tab switching)
    refetchOnReconnect: true, // Refetch when network reconnects
    refetchOnMount: true, // Refetch when component mounts
  });
};

// Employee Tasks Hooks
export const useGetEmployeeTasks = (employeeId, filters = {}) => {
  return useQuery({
    queryKey: ["employeeTasks", employeeId, filters],
    queryFn: () => {
      const params = new URLSearchParams();
      // Pass status filter to backend (backend accepts both "pending" and "todo")
      if (filters.status) {
        params.append("status", filters.status);
      }
      if (filters.dueDate) params.append("dueDate", filters.dueDate);
      if (filters.priority) params.append("priority", filters.priority);
      if (filters.projectId) params.append("projectId", filters.projectId);
      if (filters.taskMonth) params.append("taskMonth", filters.taskMonth);

      return apiClient
        .get(`/tasks/employee/${employeeId}?${params.toString()}`)
        .then((res) => res.data);
    },
    enabled: !!employeeId && employeeId !== null && employeeId !== "null",
  });
};

// Get tasks assigned to an employee that are due today
export const useGetEmployeeTasksToday = (employeeId) => {
  const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD format

  return useQuery({
    queryKey: ["employeeTasksToday", employeeId, today],
    queryFn: () =>
      apiClient
        .get(`/tasks/employee/${employeeId}/today`)
        .then((res) => res.data),
    enabled: !!employeeId && employeeId !== null && employeeId !== "null",
    staleTime: 1000 * 60 * 5,
    refetchInterval: 1000 * 60 * 10,
  });
};

// Get all subtasks assigned to a specific employee
export const useGetEmployeeSubTasks = (employeeId, filters = {}) => {
  return useQuery({
    queryKey: ["employeeSubTasks", employeeId, filters],
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters.status) params.append("status", filters.status);
      if (filters.dueDate) params.append("dueDate", filters.dueDate);
      if (filters.priority) params.append("priority", filters.priority);

      return apiClient
        .get(`/subtasks/employee/${employeeId}?${params.toString()}`)
        .then((res) => res.data)
        .catch((error) => {
          // Fallback data if endpoint doesn't exist
          console.warn("Employee subtasks endpoint not available:", error);
          return { subTasks: [] };
        });
    },
    enabled: !!employeeId && employeeId !== null && employeeId !== "null", // Only run if employeeId exists and is valid
  });
};

// Get tasks + subtasks assigned to an employee that are due today
export const useGetEmployeeSubTasksToday = (employeeId) => {
  return useQuery({
    queryKey: ["employeeSubTasksToday", employeeId],
    queryFn: () => {
      return apiClient
        .get(`/tasks/employee/${employeeId}/today`)
        .then((res) => res.data)
        .catch((error) => {
          // Fallback data if endpoint doesn't exist
          console.warn(
            "Employee subtasks today endpoint not available:",
            error
          );
          return {
            tasks: [],
            subTasks: [
              // Sample combined data for demonstration
              {
                _id: "sample-task-1",
                title: "Sample Task",
                description: "Demo task fallback",
                priority: "High",
                status: "todo",
                dueDate: new Date().toISOString(),
                timeEstimate: 2,
                project: {
                  _id: "sample-project-1",
                  name: "Sample-Project",
                  displayName: "Sample Project",
                },
              },
              {
                _id: "sample-subtask-1",
                title: "Sample Subtask",
                description: "Demo subtask fallback",
                priority: "Medium",
                status: "in-progress",
                dueDate: new Date().toISOString(),
                timeEstimate: 1.5,
                project: {
                  _id: "sample-project-2",
                  name: "Documentation-Project",
                  displayName: "Documentation Project",
                },
              },
            ],
          };
        });
    },
    enabled: !!employeeId && employeeId !== null && employeeId !== "null", // Only run if employeeId exists and is valid
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
};

export const useGetCompanyTodayTasks = (taskMonth, options = {}) => {
  const { superFilters, enabled = true } = options || {};

  return useQuery({
    queryKey: [
      "companyTodayTasks",
      taskMonth,
      JSON.stringify(superFilters || {}),
    ],
    queryFn: () => {
      const params = new URLSearchParams();
      if (taskMonth) {
        params.append("taskMonth", taskMonth);
      }
      params.append("filter", "today");
      appendSuperFiltersToParams(params, superFilters);

      const queryString = params.toString();
      const url = queryString
        ? `/tasks/company/today?${queryString}`
        : "/tasks/company/today";

      return apiClient
        .get(url)
        .then((res) => {
          const responseData = res.data || {};
          const legacyCombined = [
            ...(responseData.tasks || []),
            ...(responseData.subTasks || []),
          ];
          const filteredItems = responseData.filteredItems || legacyCombined;

          if (responseData && (responseData.tasks || responseData.subTasks)) {
            return {
              tasks: responseData.tasks || [],
              subTasks: responseData.subTasks || [],
              totalTodayTasks:
                responseData.totalTodayTasks || legacyCombined.length,
              filteredItems,
              filterOptions: responseData.filterOptions || {
                users: [],
                projects: [],
              },
            };
          }

          console.warn("Unexpected response structure:", responseData);
          return {
            tasks: [],
            subTasks: [],
            totalTodayTasks: 0,
            filteredItems: [],
            filterOptions: { users: [], projects: [] },
          };
        })
        .catch((error) => {
          console.error("Company today tasks endpoint error:", error);
          console.error("Error details:", {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
            url,
          });

          if (error.response?.status === 403) {
            console.error(
              "Access denied - user may not have admin dashboard access permission"
            );
          }

          return {
            tasks: [],
            subTasks: [],
            totalTodayTasks: 0,
            filteredItems: [],
            filterOptions: { users: [], projects: [] },
          };
        });
    },
    enabled: !!taskMonth && enabled !== false,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchInterval: 1000 * 60 * 10, // Refetch every 10 minutes
  });
};

// Company Statistics Hook - for admin dashboard overview
export const useGetCompanyStats = (companyId, taskMonth) => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1;
  return useQuery({
    queryKey: ["companyStats", companyId, taskMonth],
    queryFn: async () => {
      try {
        // Fetch multiple endpoints in parallel for company overview
        const [companyTasks, projectsThisMonth, allProjects, allEmployees] =
          await Promise.all([
            apiClient
              .get(`/tasks/company/all?taskMonth=${taskMonth}`)
              .then((res) => res.data),
            apiClient
              .get(
                `/projects/due-this-month?date=${format(
                  new Date(),
                  "yyyy-MM-dd"
                )}&active=true`
              )
              .then((res) => res.data),
            apiClient
              .get(`/projects/company/${companyId}`)
              .then((res) => res.data),
            apiClient.get("/employee").then((res) => res.data),
          ]);
        // Get task statistics from the new endpoint
        const taskStats = companyTasks?.statistics || {
          total: 0,
          completed: 0,
          inProgress: 0,
          pending: 0,
          onReview: 0,
          approved: 0,
          overdue: 0,
          today: 0,
          completionRate: 0,
          priorityDistribution: { high: 0, medium: 0, low: 0 },
        };

        // Calculate project statistics
        const allProjectsData = allProjects?.projects || [];
        const activeProjects = allProjectsData.filter(
          (project) => project.active !== false
        );
        const totalProjects = activeProjects.length;
        const projectsThisMonthData = projectsThisMonth?.projects || [];
        const projectsDueThisMonth = projectsThisMonthData.length;

        // Calculate employee statistics
        const employeesData = allEmployees?.employees || [];
        const totalEmployees = employeesData.length;
        const activeEmployees = employeesData.filter(
          (emp) => emp.active !== false
        ).length;

        // Calculate project progress average
        const projectProgressAvg =
          activeProjects.length > 0
            ? Math.round(
              activeProjects.reduce(
                (sum, project) => sum + (project.progress || 0),
                0
              ) / activeProjects.length
            )
            : 0;

        return {
          tasks: {
            total: taskStats.total,
            completed: taskStats.completed,
            inProgress: taskStats.inProgress,
            pending: taskStats.pending,
            onReview: taskStats.onReview,
            approved: taskStats.approved,
            overdue: taskStats.overdue,
            today: taskStats.today,
            completionRate: taskStats.completionRate,
          },
          projects: {
            total: totalProjects,
            dueThisMonth: projectsDueThisMonth,
            averageProgress: projectProgressAvg,
          },
          employees: {
            total: totalEmployees,
            active: activeEmployees,
          },
          overview: {
            taskCompletionRate: taskStats.completionRate,
            projectProgressAvg,
            workloadDistribution: taskStats.priorityDistribution,
          },
        };
      } catch (error) {
        // Return fallback data
        return {
          tasks: {
            total: 0,
            completed: 0,
            inProgress: 0,
            pending: 0,
            onReview: 0,
            approved: 0,
            overdue: 0,
            completionRate: 0,
          },
          projects: {
            total: 0,
            dueThisMonth: 0,
            averageProgress: 0,
            active: 0,
          },
          employees: {
            total: 0,
            active: 0,
          },
          overview: {
            taskCompletionRate: 0,
            projectProgressAvg: 0,
            workloadDistribution: {
              high: 0,
              medium: 0,
              low: 0,
            },
          },
        };
      }
    },
    enabled: !!companyId,
    staleTime: 1000 * 30, // 30 seconds
    refetchInterval: 1000 * 60 * 2, // Refetch every 2 minutes
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });
};

const appendCommaSeparatedParam = (params, key, values = []) => {
  if (!Array.isArray(values) || values.length === 0) return;
  const sanitized = values
    .map((value) =>
      value !== undefined && value !== null ? String(value).trim() : ""
    )
    .filter(Boolean);
  if (sanitized.length > 0) {
    params.append(key, sanitized.join(","));
  }
};

const appendSuperFiltersToParams = (params, filters = {}) => {
  const safeFilters = filters || {};

  if (safeFilters.search) {
    params.append("search", safeFilters.search);
  }

  appendCommaSeparatedParam(params, "status", safeFilters.status);
  appendCommaSeparatedParam(params, "priority", safeFilters.priority);
  appendCommaSeparatedParam(params, "assignedTo", safeFilters.assignedTo);
  appendCommaSeparatedParam(params, "project", safeFilters.project);

  if (safeFilters.dateRange?.start) {
    params.append("dateStart", safeFilters.dateRange.start);
  }
  if (safeFilters.dateRange?.end) {
    params.append("dateEnd", safeFilters.dateRange.end);
  }

  params.append("sortBy", safeFilters.sortBy || "dueDate");
  params.append("sortOrder", safeFilters.sortOrder || "asc");
};

// Get All Company Tasks Hook - supports backend filtering
export const useGetAllCompanyTasks = (companyId, taskMonth, options = {}) => {
  const { filter, superFilters, enabled = true } = options || {};

  return useQuery({
    queryKey: [
      "allCompanyTasks",
      companyId,
      taskMonth,
      filter || "all",
      JSON.stringify(superFilters || {}),
    ],
    queryFn: () => {
      const params = new URLSearchParams();
      if (taskMonth) {
        params.append("taskMonth", taskMonth);
      }
      if (filter) {
        params.append("filter", filter);
      }
      appendSuperFiltersToParams(params, superFilters);

      // /company/filtered returns only filteredItems + stats, not the raw
      // tasks/subTasks arrays that duplicate the same documents.
      const queryString = params.toString();
      const url = queryString
        ? `/tasks/company/filtered?${queryString}`
        : "/tasks/company/filtered";

      return apiClient.get(url).then((res) => {
        const responseData = res.data || {};
        const legacyCombined = [
          ...(responseData.tasks || []),
          ...(responseData.subTasks || []),
        ];
        const filteredItems = responseData.filteredItems || legacyCombined;

        return {
          tasks: legacyCombined,
          subTasks: responseData.subTasks || [],
          filteredItems,
          unscheduledSubTasks: responseData.unscheduledSubTasks || [],
          statistics: responseData.statistics || {},
          totalCount:
            responseData.statistics?.total ??
            filteredItems.length ??
            legacyCombined.length,
          filterOptions: responseData.filterOptions || {
            users: [],
            projects: [],
          },
        };
      });
    },
    enabled: !!companyId && !!taskMonth && enabled !== false,
  });
};

// Hook for fetching external tasks (tasks with project: null)
export const useExternalTasks = (companyId, enabled = true) => {
  return useQuery({
    queryKey: ["externalTasks", companyId],
    queryFn: async () => {
      try {
        // Server returns only project-less tasks as { _id, title }
        const response = await apiClient.get("/tasks/company/external");
        return response.data?.tasks || [];
      } catch (error) {
        // If endpoint requires admin access and user doesn't have it, return empty array
        console.warn("Failed to fetch external tasks:", error);
        return [];
      }
    },
    enabled: !!companyId && enabled,
    staleTime: 1000 * 60 * 1, // 1 minute
    cacheTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useGetCompanyTasksFiltered = (
  companyId,
  taskMonth,
  options = {}
) => {
  const { filter, superFilters, enabled = true } = options || {};

  return useQuery({
    queryKey: [
      "companyFilteredTasks",
      companyId,
      taskMonth,
      filter || "all",
      JSON.stringify(superFilters || {}),
    ],
    queryFn: () => {
      const params = new URLSearchParams();
      if (taskMonth) {
        params.append("taskMonth", taskMonth);
      }
      if (filter) {
        params.append("filter", filter);
      }
      appendSuperFiltersToParams(params, superFilters);

      const queryString = params.toString();
      const url = queryString
        ? `/tasks/company/filtered?${queryString}`
        : "/tasks/company/filtered";

      return apiClient.get(url).then((res) => {
        const responseData = res.data || {};
        return {
          filteredItems: responseData.filteredItems || [],
          filterOptions: responseData.filterOptions || {
            users: [],
            projects: [],
          },
          statistics: responseData.statistics || {},
          totalCount: responseData.filteredCount || 0,
        };
      });
    },
    enabled: !!companyId && !!taskMonth && enabled !== false,
  });
};

// Get Tasks On Review Hook - for the task-on-review page
export const useGetTasksOnReview = (filters = {}) => {
  return useQuery({
    queryKey: ["tasksOnReview", filters],
    queryFn: () => getTasksOnReview(filters),
    staleTime: 1000 * 60 * 2, // 2 minutes (frequent updates for real-time)
    refetchInterval: 1000 * 60 * 5, // Refetch every 5 minutes
    refetchOnWindowFocus: true, // Refetch when window gains focus
    refetchOnReconnect: true, // Refetch when network reconnects
    select: (data) => ({
      tasks: data?.tasks || [],
      pagination: data?.pagination || {},
      statistics: data?.statistics || {},
    }),
  });
};

// Get Tasks On Publish Hook - for the task-on-publish page
export const useGetTasksOnPublish = (filters = {}) => {
  return useQuery({
    queryKey: ["tasksOnPublish", filters],
    queryFn: () => getTasksOnPublish(filters),
    staleTime: 1000 * 60 * 2, // 2 minutes (frequent updates for real-time)
    refetchInterval: 1000 * 60 * 5, // Refetch every 5 minutes
    refetchOnWindowFocus: true, // Refetch when window gains focus
    refetchOnReconnect: true, // Refetch when network reconnects
    select: (data) => ({
      tasks: data?.tasks || [],
      pagination: data?.pagination || {},
      statistics: data?.statistics || {},
    }),
  });
};

// Get Total Unread Message Count hook
export const useGetUnreadMessageCount = () => {
  return useQuery({
    queryKey: ["unreadMessageCount"],
    queryFn: () => getUnreadCount(),
    // Poll less often — sockets already push message updates; this is a safety net.
    // On M0 (~100 ops/sec), a 30s poll from every open tab burns the shared budget.
    staleTime: 1000 * 60,
    refetchInterval: 1000 * 60 * 2,
    refetchOnWindowFocus: false,
    select: (data) => data?.count || 0,
  });
};


// Get Client Review Tasks Hook - for the client-review page
export const useGetClientReviewTasks = (filters = {}) => {
  return useQuery({
    queryKey: ["clientReviewTasks", filters],
    queryFn: () => getClientReviewTasks(filters),
    staleTime: 1000 * 60 * 2, // 2 minutes (frequent updates for real-time)
    refetchInterval: 1000 * 60 * 5, // Refetch every 5 minutes
    refetchOnWindowFocus: true, // Refetch when window gains focus
    refetchOnReconnect: true, // Refetch when network reconnects
    select: (data) => ({
      tasks: data?.tasks || [],
      pagination: data?.pagination || {},
      statistics: data?.statistics || {},
    }),
  });
};

// Badge counts for the sidebar menus. The three task endpoints return fully
// populated task + subtask lists, which the sidebar was downloading on every
// page just to read `.length`. Totals are computed server-side independently of
// pagination, so asking for a single row gives the same numbers cheaply.
export const useSidebarTaskCounts = (taskMonth, enabled = true) => {
  const shared = {
    enabled: enabled && !!taskMonth,
    staleTime: 1000 * 60 * 2,
    refetchInterval: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  };

  const onReview = useQuery({
    ...shared,
    queryKey: ["sidebarTaskCounts", "onReview", taskMonth],
    queryFn: () => getTasksOnReview({ taskMonth, limit: 1 }),
    select: (data) => data?.pagination?.totalCount || 0,
  });

  const onPublish = useQuery({
    ...shared,
    queryKey: ["sidebarTaskCounts", "onPublish", taskMonth],
    queryFn: () => getTasksOnPublish({ taskMonth, limit: 1 }),
    select: (data) =>
      data?.statistics?.publishPending ?? data?.pagination?.totalCount ?? 0,
  });

  const clientReview = useQuery({
    ...shared,
    queryKey: ["sidebarTaskCounts", "clientReview", taskMonth],
    queryFn: () => getClientReviewTasks({ taskMonth, limit: 1 }),
    select: (data) => data?.pagination?.totalCount || 0,
  });

  return {
    tasksOnReview: onReview.data || 0,
    tasksOnPublish: onPublish.data || 0,
    clientReview: clientReview.data || 0,
  };
};

export const useMarkSentToClient = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, isSubtask }) =>
      markSentToClient(itemId, { isSubtask }),
    onSuccess: () => {
      queryClient.invalidateQueries(["clientReviewTasks"]);
    },
  });
};

// Get Unscheduled Tasks Hook - for subtasks without startDate and dueDate
export const useGetUnscheduledTasks = (filters = {}) => {
  return useQuery({
    queryKey: ["unscheduledTasks", filters],
    queryFn: () => getUnscheduledTasks(filters),
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchInterval: 1000 * 60 * 5, // Refetch every 5 minutes
    refetchOnWindowFocus: true, // Refetch when window gains focus
    refetchOnReconnect: true, // Refetch when network reconnects
    select: (data) => ({
      subTasks: data?.subTasks || [],
      pagination: data?.pagination || {},
      statistics: data?.statistics || {},
    }),
  });
};

// Schedule SubTask Hook - for scheduling unscheduled subtasks
export const useScheduleSubTask = (onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ subTaskId, startDate, dueDate }) =>
      scheduleSubTask(subTaskId, startDate, dueDate),
    onSuccess: (data, variables) => {
      // Invalidate unscheduled tasks query to refresh the list
      queryClient.invalidateQueries(["unscheduledTasks"]);
      // Invalidate calendar data to refresh calendar view
      queryClient.invalidateQueries(["calendarData"]);
      // Invalidate any subtask-specific queries
      queryClient.invalidateQueries(["subtasks"]);
      if (onSuccess) onSuccess(data, variables);
    },
    onError: (error) => {
      console.error("Failed to schedule subtask:", error);
    },
  });
};

export const useDeleteEmployee = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["deleteEmployee"],
    mutationFn: (employeeId) =>
      apiClient.delete(`/employee/${employeeId}`).then((res) => res.data),
    onSuccess: () => {
      // Invalidate all employee-related queries
      queryClient.invalidateQueries(["employees"]);
      queryClient.invalidateQueries(["employee"]);
      queryClient.invalidateQueries(["employeeProjects"]);
      queryClient.invalidateQueries(["employeeTeams"]);
      queryClient.invalidateQueries(["employeeVacations"]);
      queryClient.invalidateQueries(["employeeTasks"]);
      queryClient.invalidateQueries(["employeeTasksToday"]);
      queryClient.invalidateQueries(["employeeBirthdays"]);
      // Also invalidate project queries as team members might have changed
      queryClient.invalidateQueries(["projectDetails"]);
      queryClient.invalidateQueries(["companyProjects"]);
    },
  });
};

const invalidateEmployeeQueries = (queryClient) => {
  queryClient.invalidateQueries(["employees"]);
  queryClient.invalidateQueries(["employee"]);
  queryClient.invalidateQueries(["allEmployees"]);
};

export const useRestoreEmployee = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["restoreEmployee"],
    mutationFn: (employeeId) =>
      apiClient.patch(`/employee/${employeeId}/restore`).then((res) => res.data),
    onSuccess: () => invalidateEmployeeQueries(queryClient),
  });
};

export const usePermanentlyDeleteEmployee = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["permanentlyDeleteEmployee"],
    mutationFn: (employeeId) =>
      apiClient.delete(`/employee/${employeeId}/permanent`).then((res) => res.data),
    onSuccess: () => invalidateEmployeeQueries(queryClient),
  });
};

// SubTask Hooks
export const useCreateSubTask =(parentTaskId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["createSubTask"],
    mutationFn: (subTaskData) => createSubTask(subTaskData),
    onSuccess: () => {
      queryClient.invalidateQueries(["subTasksByParentTask", parentTaskId]);
      queryClient.invalidateQueries(["getTaskById", parentTaskId]);
      window.dispatchEvent(new Event('taskCreated'));
    },
  });
};

export const useGetSubTasksByParentTask = (parentTaskId) => {
  return useQuery({
    queryKey: ["subTasksByParentTask", parentTaskId],
    queryFn: () => getSubTasksByParentTask(parentTaskId),
    select: (data) => data?.subTasks || [],
    enabled: !!parentTaskId,
    staleTime: 0,
    refetchOnMount: "always",
    // select() + default structural sharing can skip re-renders when a
    // socket patch only changes nested status fields.
    structuralSharing: false,
  });
};

export const useGetSubTaskById = (subTaskId) => {
  return useQuery({
    queryKey: ["getSubTaskById", subTaskId],
    queryFn: () => getSubTaskById(subTaskId),
    select: (data) => data?.subTask,
    enabled: !!subTaskId,
  });
};

export const useUpdateSubTaskById = (subTaskId, parentTaskId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["updateSubTaskById", subTaskId],
    mutationFn: (updateData) => updateSubTaskById(subTaskId, updateData),
    onMutate: async (updateData) => {
      // Cancel outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries(["getSubTaskById", subTaskId]);
      await queryClient.cancelQueries(["subTasksByParentTask", parentTaskId]);
      await queryClient.cancelQueries(["getTaskById", parentTaskId]);

      // Snapshot the previous values
      const previousSubTask = queryClient.getQueryData(["getSubTaskById", subTaskId]);
      const previousSubTasks = queryClient.getQueryData(["subTasksByParentTask", parentTaskId]);
      const previousTask = queryClient.getQueryData(["getTaskById", parentTaskId]);

      // Optimistically update getTaskById
      if (previousTask) {
        queryClient.setQueryData(["getTaskById", parentTaskId], (old) => {
          if (!old) return old;
          const oldTask = old.task || old;
          if (oldTask.subTasks) {
            const newSubTasks = oldTask.subTasks.map(st => 
              st._id === subTaskId ? { ...st, ...updateData } : st
            );
            return old.task ? { ...old, task: { ...old.task, subTasks: newSubTasks } } : { ...old, subTasks: newSubTasks };
          }
          return old;
        });
      }

      // Optimistically update subTasksByParentTask
      if (previousSubTasks) {
        queryClient.setQueryData(["subTasksByParentTask", parentTaskId], (old) => {
          if (!old) return old;
          const oldArray = Array.isArray(old) ? old : old.subTasks;
          if (!oldArray) return old;
          const newArray = oldArray.map(st => 
            st._id === subTaskId ? { ...st, ...updateData } : st
          );
          return Array.isArray(old) ? newArray : { ...old, subTasks: newArray };
        });
      }

      // Optimistically update getSubTaskById
      if (previousSubTask) {
        queryClient.setQueryData(["getSubTaskById", subTaskId], (old) => {
          if (!old) return old;
          return { ...old, ...updateData };
        });
      }

      return { previousSubTask, previousSubTasks, previousTask };
    },
    onError: (err, updateData, context) => {
      if (context?.previousSubTask) {
        queryClient.setQueryData(["getSubTaskById", subTaskId], context.previousSubTask);
      }
      if (context?.previousSubTasks) {
        queryClient.setQueryData(["subTasksByParentTask", parentTaskId], context.previousSubTasks);
      }
      if (context?.previousTask) {
        queryClient.setQueryData(["getTaskById", parentTaskId], context.previousTask);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries(["getSubTaskById", subTaskId]);
      queryClient.invalidateQueries(["subTasksByParentTask", parentTaskId]);
      queryClient.invalidateQueries(["getTaskById", parentTaskId]);
      queryClient.invalidateQueries({ queryKey: ["performance"] });
    },
  });
};

export const useCreateReworkEvent = (parentTaskId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["createReworkEvent", parentTaskId],
    mutationFn: (payload) => createReworkEvent(parentTaskId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries(["subTasksByParentTask", parentTaskId]);
      queryClient.invalidateQueries(["getTaskById", parentTaskId]);
      queryClient.invalidateQueries({ queryKey: ["performance"] });
    },
  });
};

export const useGetReworkCandidates = (parentTaskId, originSubTaskId, source, enabled = false) => {
  return useQuery({
    queryKey: ["reworkCandidates", parentTaskId, originSubTaskId, source],
    queryFn: () => getReworkCandidates(parentTaskId, { originSubTaskId, source }),
    enabled: !!parentTaskId && !!enabled,
  });
};

export const useDeleteSubTask = (parentTaskId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["deleteSubTask"],
    mutationFn: (subTaskId) => deleteSubTask(subTaskId),
    onSuccess: () => {
      queryClient.invalidateQueries(["subTasksByParentTask", parentTaskId]);
      queryClient.invalidateQueries(["getTaskById", parentTaskId]);
    },
  });
};

export const useAddSubTaskAttachments = (subTaskId, parentTaskId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["addSubTaskAttachments", subTaskId],
    mutationFn: (attachments) => addSubTaskAttachments(subTaskId, attachments),
    onSuccess: () => {
      queryClient.invalidateQueries(["getSubTaskById", subTaskId]);
      queryClient.invalidateQueries(["subTasksByParentTask", parentTaskId]);
    },
  });
};

export const useRemoveSubTaskAttachment = (subTaskId, parentTaskId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["removeSubTaskAttachment", subTaskId],
    mutationFn: (attachmentId) =>
      removeSubTaskAttachment(subTaskId, attachmentId),
    onSuccess: () => {
      queryClient.invalidateQueries(["getSubTaskById", subTaskId]);
      queryClient.invalidateQueries(["subTasksByParentTask", parentTaskId]);
    },
  });
};

// Notification Hooks
const updateNotificationCachesAsAllRead = (queryClient) => {
  queryClient.setQueriesData({ queryKey: ["notifications"] }, (oldData) => {
    if (!oldData?.notifications) return oldData;

    return {
      ...oldData,
      unreadCount: 0,
      notifications: oldData.notifications.map((notification) =>
        notification.read ? notification : { ...notification, read: true }
      ),
    };
  });

  queryClient.setQueryData(["unreadNotificationCount"], (oldData) => ({
    ...(oldData || { success: true }),
    count: 0,
  }));
};

const updateNotificationCachesAsRead = (queryClient, notificationId) => {
  queryClient.setQueriesData({ queryKey: ["notifications"] }, (oldData) => {
    if (!oldData?.notifications) return oldData;

    const notification = oldData.notifications.find(
      (item) => item._id === notificationId
    );
    if (!notification || notification.read) return oldData;

    return {
      ...oldData,
      unreadCount: Math.max(0, (oldData.unreadCount || 0) - 1),
      notifications: oldData.notifications.map((item) =>
        item._id === notificationId ? { ...item, read: true } : item
      ),
    };
  });

  queryClient.setQueryData(["unreadNotificationCount"], (oldData) => ({
    ...(oldData || { success: true }),
    count: Math.max(0, (oldData?.count || 0) - 1),
  }));
};

export const useGetNotifications = (limit = 10) => {
  return useQuery({
    queryKey: ["notifications", limit],
    queryFn: () => {
      // Fallback data while API is being developed
      const mockNotifications = [
        {
          _id: "1",
          type: "task_assigned",
          title: "New Task Assigned",
          message:
            "You have been assigned to 'Review project requirements' in Mobile App Development",
          data: {
            taskId: "task_1",
            taskTitle: "Review project requirements",
            projectName: "Mobile App Development",
            assignedBy: {
              name: "John Smith",
              profileImage: "/image/photo.png",
            },
          },
          read: false,
          createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2 hours ago
        },
        {
          _id: "2",
          type: "message",
          title: "New Message",
          message: "Emily Tyler sent you a message in Research project",
          data: {
            senderId: "emp_1",
            senderName: "Emily Tyler",
            senderImage: "/image/photo.png",
            conversationId: "conv_1",
            messagePreview:
              "Can we schedule a meeting to discuss the project timeline?",
          },
          read: false,
          createdAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(), // 3 hours ago
        },
        {
          _id: "3",
          type: "task_updated",
          title: "Task Status Updated",
          message: "Task 'Update documentation' has been marked as completed",
          data: {
            taskId: "task_2",
            taskTitle: "Update documentation",
            projectName: "Documentation Project",
            updatedBy: {
              name: "Sarah Wilson",
              profileImage: "/image/photo.png",
            },
            oldStatus: "in-progress",
            newStatus: "completed",
          },
          read: false,
          createdAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(), // 5 hours ago
        },
        {
          _id: "4",
          type: "deadline_reminder",
          title: "Deadline Reminder",
          message: "Task 'Fix login bug' is due in 2 hours",
          data: {
            taskId: "task_3",
            taskTitle: "Fix login bug",
            projectName: "Bug Fixes",
            dueDate: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(),
          },
          read: true,
          createdAt: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(), // 6 hours ago
        },
        {
          _id: "5",
          type: "project_update",
          title: "Project Status Update",
          message: "Website Redesign project progress updated to 85%",
          data: {
            projectId: "proj_1",
            projectName: "Website Redesign",
            oldProgress: 80,
            newProgress: 85,
            updatedBy: {
              name: "Mike Johnson",
              profileImage: "/image/photo.png",
            },
          },
          read: true,
          createdAt: new Date(
            Date.now() - 1 * 24 * 60 * 60 * 1000
          ).toISOString(), // 1 day ago
        },
        {
          _id: "6",
          type: "comment",
          title: "New Comment",
          message: "Alex Brown commented on your task 'Code review'",
          data: {
            taskId: "task_4",
            taskTitle: "Code review",
            projectName: "Sample Project",
            commentBy: {
              name: "Alex Brown",
              profileImage: "/image/photo.png",
            },
            commentPreview:
              "Great work on the implementation! Just a few minor suggestions...",
          },
          read: true,
          createdAt: new Date(
            Date.now() - 2 * 24 * 60 * 60 * 1000
          ).toISOString(), // 2 days ago
        },
      ];

      return getUserNotifications(limit).catch(() => ({
        notifications: mockNotifications.slice(0, limit),
        totalCount: mockNotifications.length,
        unreadCount: mockNotifications.filter((n) => !n.read).length,
      }));
    },
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
    // Safety net: sockets can drop silently, so keep notifications self-healing.
    refetchInterval: 1000 * 60 * 2,
  });
};

export const useGetUnreadNotificationCount = () => {
  return useQuery({
    queryKey: ["unreadNotificationCount"],
    queryFn: () => {
      return getUnreadNotificationCount().catch(() => ({ count: 3 }));
    },
    staleTime: 1000 * 60,
    refetchInterval: 1000 * 60 * 2,
    refetchOnWindowFocus: false,
  });
};

export const useMarkNotificationAsRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["markNotificationAsRead"],
    mutationFn: (notificationId) => markNotificationAsRead(notificationId),
    onSuccess: (_, notificationId) => {
      updateNotificationCachesAsRead(queryClient, notificationId);
    },
  });
};

export const useMarkAllNotificationsAsRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["markAllNotificationsAsRead"],
    mutationFn: () => markAllNotificationsAsRead(),
    onSuccess: () => {
      updateNotificationCachesAsAllRead(queryClient);
    },
  });
};

////////////  STICKY NOTES HOOKS ⚠️⚠️⚠️⚠️⚠️ ////////////////////

// Get all sticky notes for the current user
export const useGetStickyNotes = (options = {}) => {
  return useQuery({
    queryKey: ["stickyNotes", options],
    queryFn: () => getUserStickyNotes(options),
    select: (data) => data?.stickyNotes || [],
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchOnWindowFocus: false,
  });
};

// Create a new sticky note
export const useCreateStickyNote = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["createStickyNote"],
    mutationFn: (noteData) => createStickyNote(noteData),
    onSuccess: () => {
      queryClient.invalidateQueries(["stickyNotes"]);
    },
  });
};

// Get a specific sticky note by ID
export const useGetStickyNoteById = (noteId) => {
  return useQuery({
    queryKey: ["stickyNote", noteId],
    queryFn: () => getStickyNoteById(noteId),
    select: (data) => data?.stickyNote,
    enabled: !!noteId,
  });
};

// Update a sticky note
export const useUpdateStickyNote = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["updateStickyNote"],
    mutationFn: ({ noteId, updateData }) =>
      updateStickyNote(noteId, updateData),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(["stickyNotes"]);
      queryClient.invalidateQueries(["stickyNote", variables.noteId]);
    },
  });
};

// Delete a sticky note
export const useDeleteStickyNote = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["deleteStickyNote"],
    mutationFn: (noteId) => deleteStickyNote(noteId),
    onSuccess: () => {
      queryClient.invalidateQueries(["stickyNotes"]);
    },
  });
};

// Update sticky note positions (for drag and drop)
export const useUpdateStickyNotePositions = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["updateStickyNotePositions"],
    mutationFn: (noteIds) => updateStickyNotePositions(noteIds),
    onSuccess: () => {
      queryClient.invalidateQueries(["stickyNotes"]);
    },
  });
};

// Archive/Unarchive a sticky note
export const useToggleArchiveStickyNote = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["toggleArchiveStickyNote"],
    mutationFn: (noteId) => toggleArchiveStickyNote(noteId),
    onSuccess: () => {
      queryClient.invalidateQueries(["stickyNotes"]);
      queryClient.invalidateQueries(["archivedStickyNotes"]);
    },
  });
};

// Set reminder for a sticky note
export const useSetStickyNoteReminder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["setStickyNoteReminder"],
    mutationFn: ({ noteId, reminderDate }) =>
      setStickyNoteReminder(noteId, reminderDate),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(["stickyNotes"]);
      queryClient.invalidateQueries(["stickyNote", variables.noteId]);
      queryClient.invalidateQueries(["stickyNotesWithReminders"]);
    },
  });
};

// Clear reminder for a sticky note
export const useClearStickyNoteReminder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["clearStickyNoteReminder"],
    mutationFn: (noteId) => clearStickyNoteReminder(noteId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(["stickyNotes"]);
      queryClient.invalidateQueries(["stickyNote", variables]);
      queryClient.invalidateQueries(["stickyNotesWithReminders"]);
    },
  });
};

// Get archived sticky notes
export const useGetArchivedStickyNotes = (options = {}) => {
  return useQuery({
    queryKey: ["archivedStickyNotes", options],
    queryFn: () => getArchivedStickyNotes(options),
    select: (data) => data?.stickyNotes || [],
  });
};

// Get sticky notes with active reminders
export const useGetStickyNotesWithReminders = () => {
  return useQuery({
    queryKey: ["stickyNotesWithReminders"],
    queryFn: () => getStickyNotesWithReminders(),
    select: (data) => data?.stickyNotes || [],
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchInterval: 1000 * 60 * 10, // Refetch every 10 minutes
  });
};

// Bulk delete sticky notes
export const useBulkDeleteStickyNotes = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["bulkDeleteStickyNotes"],
    mutationFn: (noteIds) => bulkDeleteStickyNotes(noteIds),
    onSuccess: () => {
      queryClient.invalidateQueries(["stickyNotes"]);
    },
  });
};

////////////  ATTENDANCE HOOKS ⚠️⚠️⚠️⚠️⚠️ ////////////////////

const mergeTodayAttendance = (current, attendance) => {
  const today = Array.isArray(current?.today) ? [...current.today] : [];
  if (!attendance?._id) return today;
  const index = today.findIndex((item) => String(item._id) === String(attendance._id));
  if (index >= 0) today[index] = { ...today[index], ...attendance };
  else today.push(attendance);
  return today;
};

const cacheAttendanceStatus = (queryClient, attendance) => {
  if (!attendance) return;
  queryClient.setQueryData(["attendanceStatus"], (current) => ({
    ...(current || {}),
    success: true,
    attendance,
    status: attendance.status,
    today: mergeTodayAttendance(current, attendance),
  }));
};

// Clock in - Start attendance
export const useClockIn = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["clockIn"],
    mutationFn: (attendanceData) => clockIn(attendanceData),
    retry: (failureCount, error) => {
      // Don't retry on 400 errors (client errors like "already clocked in")
      if (error?.response?.status === 400) {
        return false;
      }
      // Retry other errors up to 1 time
      return failureCount < 1;
    },
    retryDelay: 1000,
    onSuccess: (result) => {
      cacheAttendanceStatus(queryClient, result?.attendance);
      queryClient.invalidateQueries(["attendanceStatus"]);
      queryClient.invalidateQueries(["employeeAttendanceHistory"]);
      queryClient.invalidateQueries(["employeeAttendance"]);
      queryClient.invalidateQueries(["dailyAttendanceReport"]);
    },
  });
};

// Clock out - End attendance
export const useClockOut = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["clockOut"],
    mutationFn: (clockOutData) => clockOut(clockOutData),
    retry: (failureCount, error) => {
      // Don't retry on 400 errors (client errors like "no active attendance")
      if (error?.response?.status === 400) {
        return false;
      }
      // Retry other errors up to 1 time
      return failureCount < 1;
    },
    retryDelay: 1000,
    onSuccess: (result) => {
      const attendance = result?.attendance;
      if (attendance) cacheAttendanceStatus(queryClient, attendance);
      else {
        queryClient.setQueryData(["attendanceStatus"], (current) => {
          const prev = current || {};
          const next = prev.attendance
            ? { ...prev.attendance, status: "checked-out" }
            : { status: "checked-out" };
          return {
            ...prev,
            success: true,
            status: "checked-out",
            attendance: next,
            today: mergeTodayAttendance(prev, next),
          };
        });
      }
      queryClient.invalidateQueries(["attendanceStatus"]);
      queryClient.invalidateQueries(["employeeAttendanceHistory"]);
      queryClient.invalidateQueries(["employeeAttendance"]);
      queryClient.invalidateQueries(["dailyAttendanceReport"]);
    },
  });
};

// Start break
export const useStartBreak = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["startBreak"],
    mutationFn: (breakData) => startBreak(breakData),
    retry: (failureCount, error) => {
      // Don't retry on 400 errors (client errors)
      if (error?.response?.status === 400) {
        return false;
      }
      // Retry other errors up to 1 time
      return failureCount < 1;
    },
    retryDelay: 1000,
    onSuccess: (result) => {
      cacheAttendanceStatus(queryClient, result?.attendance);
      queryClient.invalidateQueries(["attendanceStatus"]);
      queryClient.invalidateQueries(["employeeAttendance"]);
    },
  });
};

// End break
export const useEndBreak = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["endBreak"],
    mutationFn: () => endBreak(),
    retry: (failureCount, error) => {
      // Don't retry on 400 errors (client errors)
      if (error?.response?.status === 400) {
        return false;
      }
      // Retry other errors up to 1 time
      return failureCount < 1;
    },
    retryDelay: 1000,
    onSuccess: (result) => {
      cacheAttendanceStatus(queryClient, result?.attendance);
      queryClient.invalidateQueries(["attendanceStatus"]);
      queryClient.invalidateQueries(["employeeAttendance"]);
    },
  });
};

// Get current attendance status
export const useGetCurrentAttendanceStatus = () => {
  const queryClient = useQueryClient();
  const hasToken =
    typeof window !== "undefined" && Boolean(localStorage.getItem("token"));

  // Break / end shift done on another device (e.g. the mobile PWA) arrives
  // over the socket, so refetch instead of waiting for a page refresh.
  useEffect(() => {
    const handleAttendanceUpdated = () => {
      queryClient.invalidateQueries({ queryKey: ["attendanceStatus"] });
      queryClient.invalidateQueries({ queryKey: ["employeeAttendance"] });
      queryClient.invalidateQueries({ queryKey: ["employeeAttendanceHistory"] });
    };
    socketService.onAttendanceUpdated(handleAttendanceUpdated);
    return () => socketService.offAttendanceUpdated(handleAttendanceUpdated);
  }, [queryClient]);

  return useQuery({
    queryKey: ["attendanceStatus"],
    queryFn: () => getCurrentAttendanceStatus(),
    enabled: hasToken,
    staleTime: 0,
    refetchOnMount: "always",
    // Catches changes missed while the tab/PWA was in the background.
    refetchOnWindowFocus: true,
    refetchInterval: 1000 * 60 * 2,
  });
};

// Get employee attendance history
export const useGetEmployeeAttendanceHistory = (
  employeeId,
  queryParams = {}
) => {
  return useQuery({
    queryKey: ["employeeAttendanceHistory", employeeId, queryParams],
    queryFn: () => getEmployeeAttendanceHistory(employeeId, queryParams),
    enabled: !!employeeId,
    keepPreviousData: true,
  });
};

// Get daily attendance report (Admin only)
export const useGetDailyAttendanceReport = (date = null) => {
  return useQuery({
    queryKey: ["dailyAttendanceReport", date],
    queryFn: () => getDailyAttendanceReport(date),
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchInterval: 1000 * 60 * 10, // Refetch every 10 minutes
  });
};

// Get attendance summary (Admin only)
export const useGetAttendanceSummary = (queryParams, enabled = true) => {
  return useQuery({
    queryKey: ["attendanceSummary", queryParams],
    queryFn: () => getAttendanceSummary(queryParams),
    enabled: enabled && !!queryParams?.startDate && !!queryParams?.endDate,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

// Get attendance analytics (Admin only)
export const useGetAttendanceAnalytics = (period = "month") => {
  return useQuery({
    queryKey: ["attendanceAnalytics", period],
    queryFn: () => getAttendanceAnalytics(period),
    staleTime: 1000 * 60 * 10, // 10 minutes
    refetchInterval: 1000 * 60 * 15, // Refetch every 15 minutes
  });
};

// Update attendance record (Admin only)
export const useUpdateAttendanceRecord = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["updateAttendanceRecord"],
    mutationFn: ({ attendanceId, updateData }) =>
      updateAttendanceRecord(attendanceId, updateData),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(["employeeAttendanceHistory"]);
      queryClient.invalidateQueries(["dailyAttendanceReport"]);
      queryClient.invalidateQueries(["attendanceSummary"]);
      queryClient.invalidateQueries(["attendanceAnalytics"]);

      // If this is today's attendance, also update current status
      const today = new Date().toDateString();
      const recordDate = new Date(data.attendance?.date).toDateString();
      if (today === recordDate) {
        queryClient.invalidateQueries(["attendanceStatus"]);
      }
    },
  });
};

// Approve/Reject attendance (Admin only)
export const useApproveAttendance = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["approveAttendance"],
    mutationFn: ({ attendanceId, approvalData }) =>
      approveAttendance(attendanceId, approvalData),
    onSuccess: () => {
      queryClient.invalidateQueries(["employeeAttendanceHistory"]);
      queryClient.invalidateQueries(["dailyAttendanceReport"]);
      queryClient.invalidateQueries(["attendanceSummary"]);
      queryClient.invalidateQueries(["attendanceAnalytics"]);
    },
  });
};

// Delete attendance record (Admin only)
export const useDeleteAttendanceRecord = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["deleteAttendanceRecord"],
    mutationFn: (attendanceId) => deleteAttendanceRecord(attendanceId),
    onSuccess: () => {
      queryClient.invalidateQueries(["employeeAttendanceHistory"]);
      queryClient.invalidateQueries(["dailyAttendanceReport"]);
      queryClient.invalidateQueries(["attendanceSummary"]);
      queryClient.invalidateQueries(["attendanceAnalytics"]);
      queryClient.invalidateQueries(["attendanceStatus"]);
    },
  });
};

// Company-wide delete hooks
export const useDeleteAllCompanyTasks = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["deleteAllCompanyTasks"],
    mutationFn: () => deleteAllCompanyTasks(),
    onSuccess: () => {
      // Invalidate all task-related queries
      queryClient.invalidateQueries(["projectDetails"]);
      queryClient.invalidateQueries(["companyProjects"]);
      queryClient.invalidateQueries(["employeeTasks"]);
      queryClient.invalidateQueries(["employeeTasksToday"]);
      queryClient.invalidateQueries(["tasksDueInMonth"]);
      queryClient.invalidateQueries(["companyTasks"]);
    },
  });
};

export const useDeleteAllCompanyProjects = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["deleteAllCompanyProjects"],
    mutationFn: () => deleteAllCompanyProjects(),
    onSuccess: () => {
      // Invalidate all project-related queries
      queryClient.invalidateQueries(["projectDetails"]);
      queryClient.invalidateQueries(["companyProjects"]);
      queryClient.invalidateQueries(["employeeProjects"]);
      queryClient.invalidateQueries(["projectAnalytics"]);
    },
  });
};

export const useDeleteAllCompanyEmployees = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["deleteAllCompanyEmployees"],
    mutationFn: () => deleteAllCompanyEmployees(),
    onSuccess: () => {
      // Invalidate all employee-related queries
      queryClient.invalidateQueries(["employees"]);
      queryClient.invalidateQueries(["employee"]);
      queryClient.invalidateQueries(["employeeProjects"]);
      queryClient.invalidateQueries(["employeeTeams"]);
      queryClient.invalidateQueries(["employeeVacations"]);
      queryClient.invalidateQueries(["employeeTasks"]);
      queryClient.invalidateQueries(["employeeTasksToday"]);
      queryClient.invalidateQueries(["employeeBirthdays"]);
      // Also invalidate project queries as team members might have changed
      queryClient.invalidateQueries(["projectDetails"]);
      queryClient.invalidateQueries(["companyProjects"]);
    },
  });
};

// Custom hook for managing attendance state
export const useAttendanceManager = () => {
  const { user } = useAuth();
  const { data: currentStatus, isLoading: statusLoading } =
    useGetCurrentAttendanceStatus();
  const { data: todaySubTasks, isLoading: tasksLoading } = useGetEmployeeSubTasksToday(
    user?._id || null
  );

  const clockInMutation = useClockIn();
  const clockOutMutation = useClockOut();
  const startBreakMutation = useStartBreak();
  const endBreakMutation = useEndBreak();

  // Use refs to prevent concurrent calls (faster than isPending check)
  const isClockInInProgress = useRef(false);
  const isClockOutInProgress = useRef(false);
  const isStartBreakInProgress = useRef(false);
  const isEndBreakInProgress = useRef(false);

  const isShiftActive =
    currentStatus?.attendance?.status === "checked-in" ||
    currentStatus?.attendance?.status === "break";

  const isOnBreak = currentStatus?.attendance?.status === "break";

  // Calculate if end shift is blocked by pending tasks without reasons.
  // That is anything due today plus anything already overdue; the API's overdue
  // list leaves out items that got a reason today (they move into subTasks).
  // In-progress work that is due later never blocks.
  const pendingTasksWithoutReason = [
    ...(todaySubTasks?.subTasks || []).filter(
      (task) =>
        !task.hasPendingReasonToday &&
        !["on-review", "approved", "completed", "client-approved"].includes(task.status)
    ),
    ...(todaySubTasks?.overdue || []),
  ];

  const isEndShiftBlocked = pendingTasksWithoutReason.length > 0;
  const pendingTasksWithoutReasonCount = pendingTasksWithoutReason.length;

  const shiftStartTime = currentStatus?.attendance?.clockInTime
    ? new Date(currentStatus.attendance.clockInTime)
    : null;

  const handleClockIn = async (locationData = {}, deviceInfo = {}) => {
    // ... no changes to handleClockIn ...
    // Prevent multiple simultaneous calls using ref (immediate check)
    if (isClockInInProgress.current || clockInMutation.isPending) {
      console.log("Clock in already in progress, ignoring request");
      return { success: false, message: "Clock in already in progress" };
    }

    // Set flag immediately to prevent concurrent calls
    isClockInInProgress.current = true;

    try {
      const attendanceData = {
        location: locationData,
        deviceInfo: {
          browser: navigator.userAgent,
          device: "Web",
          ...deviceInfo,
        },
      };

      console.log("Executing clock in mutation with data:", attendanceData);
      const result = await clockInMutation.mutateAsync(attendanceData);
      console.log("Clock in mutation result:", result);
      return result;
    } catch (error) {
      console.error("Failed to clock in:", error);
      throw error;
    } finally {
      // Reset flag after completion (success or error)
      isClockInInProgress.current = false;
    }
  };

  const handleClockOut = async (locationData = {}, workDescription = "") => {
    // If blocked, prevent clock out
    if (isEndShiftBlocked) {
      throw new Error(`Cannot end shift. You have ${pendingTasksWithoutReasonCount} pending task(s) without a reason.`);
    }

    // Prevent multiple simultaneous calls using ref (immediate check)
    if (isClockOutInProgress.current || clockOutMutation.isPending) {
      console.log("Clock out already in progress, ignoring request");
      return { success: false, message: "Clock out already in progress" };
    }

    // Set flag immediately to prevent concurrent calls
    isClockOutInProgress.current = true;

    try {
      const clockOutData = {
        location: locationData,
        workDescription,
      };

      console.log("Executing clock out mutation with data:", clockOutData);
      const result = await clockOutMutation.mutateAsync(clockOutData);
      console.log("Clock out mutation result:", result);
      return result;
    } catch (error) {
      console.error("Failed to clock out:", error);
      throw error;
    } finally {
      // Reset flag after completion (success or error)
      isClockOutInProgress.current = false;
    }
  };

  const handleStartBreak = async (reason = "") => {
    // ... no changes to handleStartBreak ...
    // Prevent multiple simultaneous calls using ref (immediate check)
    if (isStartBreakInProgress.current || startBreakMutation.isPending) {
      console.log("Start break already in progress, ignoring request");
      return { success: false, message: "Start break already in progress" };
    }

    // Set flag immediately to prevent concurrent calls
    isStartBreakInProgress.current = true;

    try {
      const result = await startBreakMutation.mutateAsync({ reason });
      return result;
    } catch (error) {
      console.error("Failed to start break:", error);
      throw error;
    } finally {
      // Reset flag after completion (success or error)
      isStartBreakInProgress.current = false;
    }
  };

  const handleEndBreak = async () => {
    // Prevent multiple simultaneous calls using ref (immediate check)
    if (isEndBreakInProgress.current || endBreakMutation.isPending) {
      console.log("End break already in progress, ignoring request");
      return { success: false, message: "End break already in progress" };
    }

    // Set flag immediately to prevent concurrent calls
    isEndBreakInProgress.current = true;

    try {
      const result = await endBreakMutation.mutateAsync();
      return result;
    } catch (error) {
      console.error("Failed to end break:", error);
      throw error;
    } finally {
      // Reset flag after completion (success or error)
      isEndBreakInProgress.current = false;
    }
  };

  return {
    // Status data
    currentStatus: currentStatus?.attendance,
    todayRecords: currentStatus?.today || [],
    isShiftActive,
    isOnBreak,
    shiftStartTime,
    statusLoading,
    isEndShiftBlocked,
    pendingTasksWithoutReasonCount,
    pendingTasksWithoutReason,
    todaySubTasks,

    // Actions
    handleClockIn,
    handleClockOut,
    handleStartBreak,
    handleEndBreak,

    // Loading states
    isClockingIn: clockInMutation.isPending,
    isClockingOut: clockOutMutation.isPending,
    isStartingBreak: startBreakMutation.isPending,
    isEndingBreak: endBreakMutation.isPending,

    // Error states
    clockInError: clockInMutation.error,
    clockOutError: clockOutMutation.error,
    startBreakError: startBreakMutation.error,
    endBreakError: endBreakMutation.error,
  };
};

// Get project social media details
export const useGetProjectSocialMedia = (projectId) => {
  return useQuery({
    queryKey: ["projectSocialMedia", projectId],
    queryFn: () =>
      apiClient
        .get(`/projects/${projectId}/social-media`)
        .then((res) => res.data),
    enabled: !!projectId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    cacheTime: 1000 * 60 * 10, // 10 minutes
  });
};

export const useGlobalSearch = (query) => {
  return useQuery({
    queryKey: ["globalSearch", query],
    queryFn: () =>
      apiClient.get(`/search?query=${query}`).then((res) => res.data?.results),
    enabled: !!query && query.length >= 2,
    staleTime: 1000 * 60, // 1 minute
  });
};

export const useGetServerUsage = () => {
  return useQuery({
    queryKey: ["serverUsage"],
    queryFn: () =>
      apiClient.get("/system-settings/usage").then((res) => res.data?.data),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

// Project Field Hooks
export const useGetProjectFields = (companyId) => {
  return useQuery({
    queryKey: ["projectFields", companyId],
    queryFn: () => getProjectFields(companyId),
    enabled: !!companyId,
    select: (data) => data?.fields || [],
  });
};

export const useCreateProjectField = (companyId, onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fieldData) => createProjectField(companyId, fieldData),
    onSuccess: (...args) => {
      queryClient.invalidateQueries(["projectFields", companyId]);
      if (onSuccess) onSuccess(...args);
    },
  });
};

export const useUpdateProjectField = (companyId, onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ fieldId, fieldData }) =>
      updateProjectField(companyId, fieldId, fieldData),
    onSuccess: (...args) => {
      queryClient.invalidateQueries(["projectFields", companyId]);
      if (onSuccess) onSuccess(...args);
    },
  });
};

export const useDeleteProjectField = (companyId, onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fieldId) => deleteProjectField(companyId, fieldId),
    onSuccess: (...args) => {
      queryClient.invalidateQueries(["projectFields", companyId]);
      if (onSuccess) onSuccess(...args);
    },
  });
};

export const useReorderProjectFields = (companyId, onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fieldIds) => reorderProjectFields(companyId, fieldIds),
    onSuccess: (...args) => {
      queryClient.invalidateQueries(["projectFields", companyId]);
      if (onSuccess) onSuccess(...args);
    },
  });
};

export const useSubmitPendingReason = (subTaskId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reason) => submitSubTaskPendingReason(subTaskId, reason),
    onSuccess: async () => {
      await queryClient.invalidateQueries(["subTask", subTaskId]);
      await queryClient.invalidateQueries(["employeeSubTasksToday"]);
    },
  });
};

// Lead Form Config Hooks (backward compatible — returns active form)
export const useGetLeadFormConfig = () => {
  return useQuery({
    queryKey: ["leadFormConfig"],
    queryFn: () => apiClient.get("/leads/settings/form-config").then((res) => res.data),
  });
};

export const useUpdateLeadFormConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (config) =>
      apiClient
        .put("/leads/settings/form-config", { fields: config })
        .then((res) => res.data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["leadFormConfig"] });
      await queryClient.invalidateQueries({ queryKey: ["leadFormConfigs"] });
    },
  });
};

// Multi-Form Config Hooks
export const useGetAllFormConfigs = () => {
  return useQuery({
    queryKey: ["leadFormConfigs"],
    queryFn: () => apiClient.get("/leads/settings/form-configs").then((res) => res.data),
  });
};

export const useCreateFormConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (formData) =>
      apiClient.post("/leads/settings/form-configs", formData).then((res) => res.data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["leadFormConfigs"] });
    },
  });
};

export const useGetFormConfigById = (configId) => {
  return useQuery({
    queryKey: ["formConfigById", configId],
    queryFn: () =>
      apiClient.get(`/leads/settings/form-configs/${configId}`).then((res) => res.data),
    enabled: !!configId,
  });
};

export const useUpdateFormConfigById = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ configId, ...data }) =>
      apiClient
        .put(`/leads/settings/form-configs/${configId}`, data)
        .then((res) => res.data),
    onSuccess: async (_, { configId }) => {
      await queryClient.invalidateQueries({ queryKey: ["leadFormConfigs"] });
      await queryClient.invalidateQueries({ queryKey: ["formConfigById", configId] });
      // Also invalidate the active-form singleton so scoring/assignment fields stay fresh
      await queryClient.invalidateQueries({ queryKey: ["leadFormConfig"] });
    },
  });
};

export const useActivateFormConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (configId) =>
      apiClient
        .put(`/leads/settings/form-configs/${configId}/activate`)
        .then((res) => res.data),
    onSuccess: async (_, configId) => {
      // Invalidate everything related to form configs to ensure total UI sync
      await queryClient.invalidateQueries({ queryKey: ["leadFormConfigs"] });
      await queryClient.invalidateQueries({ queryKey: ["leadFormConfig"] });
      await queryClient.invalidateQueries({ queryKey: ["formConfigById"] });
    },
  });
};

export const useDeactivateFormConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (configId) =>
      apiClient
        .put(`/leads/settings/form-configs/${configId}/deactivate`)
        .then((res) => res.data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["leadFormConfigs"] });
      await queryClient.invalidateQueries({ queryKey: ["leadFormConfig"] });
      await queryClient.invalidateQueries({ queryKey: ["formConfigById"] });
    },
  });
};

export const useDeleteFormConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (configId) =>
      apiClient
        .delete(`/leads/settings/form-configs/${configId}`)
        .then((res) => res.data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["leadFormConfigs"] });
    },
  });
};

// Dashboard Config Hooks
export const useGetDashboardConfig = (projectId = null) => {
  return useQuery({
    queryKey: ["dashboardConfig", projectId],
    queryFn: () => {
      const params = projectId ? { projectId } : {};
      return apiClient.get("/leads/settings/dashboard-config", { params }).then((res) => res.data);
    },
  });
};

export const useUpdateDashboardConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (configData) =>
      apiClient
        .put("/leads/settings/dashboard-config", configData)
        .then((res) => res.data),
    onSuccess: async (data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ["dashboardConfig"] });
      await queryClient.invalidateQueries({ queryKey: ["leadStats"] });
    },
  });
};


// Lead Status Hooks
export const useGetLeadStatuses = (projectId = null) => {
  return useQuery({
    queryKey: ["leadStatuses", projectId],
    queryFn: () => {
      const params = projectId ? { projectId } : {};
      return apiClient.get("/leads/settings/statuses", { params }).then((res) => res.data);
    },
  });
};

export const useCreateLeadStatus = (projectId = null) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (statusData) => {
      const data = projectId ? { ...statusData, projectId } : statusData;
      return apiClient.post("/leads/settings/statuses", data).then((res) => res.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["leadStatuses"]);
    },
  });
};

export const useUpdateLeadStatus = (projectId = null) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ statusId, statusData }) => {
      const data = projectId ? { ...statusData, projectId } : statusData;
      return apiClient
        .put(`/leads/settings/statuses/${statusId}`, data)
        .then((res) => res.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["leadStatuses"]);
    },
  });
};

export const useDeleteLeadStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (statusId) =>
      apiClient
        .delete(`/leads/settings/statuses/${statusId}`)
        .then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries(["leadStatuses"]);
    },
  });
};

////////////  HOLIDAY HOOKS ⚠️⚠️⚠️⚠️⚠️ ////////////////////

export const useGetHolidays = (companyId, year) => {
  return useQuery({
    queryKey: ["holidays", companyId, year],
    queryFn: () => getHolidays(companyId, year),
    enabled: !!companyId,
    select: (data) => data?.holidays || [],
  });
};

export const useCreateHoliday = (companyId, onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (holidayData) => createHoliday(companyId, holidayData),
    onSuccess: (...args) => {
      queryClient.invalidateQueries(["holidays", companyId]);
      if (onSuccess) onSuccess(...args);
    },
  });
};

export const useBulkCreateHolidays = (companyId, onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (holidays) => bulkCreateHolidays(companyId, holidays),
    onSuccess: (...args) => {
      queryClient.invalidateQueries(["holidays", companyId]);
      if (onSuccess) onSuccess(...args);
    },
  });
};

export const useUpdateHoliday = (companyId, onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ holidayId, holidayData }) =>
      updateHoliday(companyId, holidayId, holidayData),
    onSuccess: (...args) => {
      queryClient.invalidateQueries(["holidays", companyId]);
      if (onSuccess) onSuccess(...args);
    },
  });
};

export const useDeleteHoliday = (companyId, onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (holidayId) => deleteHoliday(companyId, holidayId),
    onSuccess: (...args) => {
      queryClient.invalidateQueries(["holidays", companyId]);
      if (onSuccess) onSuccess(...args);
    },
  });
};

export const useBulkDeleteHolidays = (companyId, onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (holidayIds) => bulkDeleteHolidays(companyId, holidayIds),
    onSuccess: (...args) => {
      queryClient.invalidateQueries(["holidays", companyId]);
      if (onSuccess) onSuccess(...args);
    },
  });
};

////////////  WORK SCHEDULE HOOKS ⚠️⚠️⚠️⚠️⚠️ ////////////////////

export const useGetWorkSchedule = (companyId) => {
  return useQuery({
    queryKey: ["workSchedule", companyId],
    queryFn: () => getWorkSchedule(companyId),
    enabled: !!companyId,
    select: (data) => data?.schedule || null,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useSaveWorkSchedule = (companyId, onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (scheduleData) => saveWorkSchedule(companyId, scheduleData),
    onSuccess: (...args) => {
      queryClient.invalidateQueries(["workSchedule", companyId]);
      if (onSuccess) onSuccess(...args);
    },
  });
};
////////////  LEAVE POLICY HOOKS ⚠️⚠️⚠️⚠️⚠️ ////////////////////

export const useGetLeavePolicy = (companyId) => {
  return useQuery({
    queryKey: ["leavePolicy", companyId],
    queryFn: () => getLeavePolicy(companyId),
    enabled: !!companyId,
    select: (data) => data?.policy || [],
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useSaveLeavePolicy = (companyId, onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (policyData) => saveLeavePolicy(companyId, policyData),
    onSuccess: (...args) => {
      queryClient.invalidateQueries(["leavePolicy", companyId]);
      if (onSuccess) onSuccess(...args);
    },
  });
};
export const useGetCompany = (companyId) => {
  return useQuery({
    queryKey: ["company", companyId],
    queryFn: () => getCompanyById(companyId),
    enabled: !!companyId,
  });
};

export const useUpdateCompany = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ companyId, data }) => updateCompany(companyId, data),
    onSuccess: (_, { companyId }) => {
      queryClient.invalidateQueries({ queryKey: ["company", companyId] });
    },
  });
};

export const useResetCompanyPerformance = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (options) => resetCompanyPerformance(options),
    onSuccess: (_, options) => {
      if (options?.dryRun) return;
      queryClient.invalidateQueries({ queryKey: ["leaderboard"] });
      queryClient.invalidateQueries({ queryKey: ["performance"] });
    },
  });
};

export const useResetEmployeePerformance = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (employeeId) => resetEmployeePerformance(employeeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leaderboard"] });
      queryClient.invalidateQueries({ queryKey: ["performance"] });
    },
  });
};

export const useUpdatePerformanceLedgerEntry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ employeeId, payload }) =>
      updatePerformanceLedgerEntry(employeeId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leaderboard"] });
      queryClient.invalidateQueries({ queryKey: ["performance"] });
    },
  });
};

export const useDeletePerformanceLedgerEntry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ employeeId, payload }) =>
      deletePerformanceLedgerEntry(employeeId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leaderboard"] });
      queryClient.invalidateQueries({ queryKey: ["performance"] });
    },
  });
};

export const useGetScheduledPosts = (params) => {
  return useQuery({
    queryKey: ["scheduledPosts", params],
    queryFn: () => getScheduledPosts(params),
  });
};

export const useDeleteScheduledPost = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => deleteScheduledPost(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scheduledPosts"] });
    },
  });
};

export const useGetPostInsights = (id) => {
  return useQuery({
    queryKey: ["postInsights", id],
    queryFn: () => getPostInsights(id),
    enabled: !!id,
  });
};

export const useGetPostComments = (id) => {
  return useQuery({
    queryKey: ["postComments", id],
    queryFn: () => getPostComments(id),
    enabled: !!id,
  });
};

export const useReplyToComment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ commentId, message, isTopLevel }) => replyToComment(commentId, message, isTopLevel),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["postComments"] });
    },
  });
};

export const useLikeComment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (commentId) => likeComment(commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["postComments"] });
    },
  });
};

export const useGetAudienceDemographics = (instagramId) => {
  return useQuery({
    queryKey: ["audienceDemographics", instagramId],
    queryFn: () => getAudienceDemographics(instagramId),
    enabled: !!instagramId,
  });
};

export const useGetProfileInteractions = (instagramId) => {
  return useQuery({
    queryKey: ["profileInteractions", instagramId],
    queryFn: () => getProfileInteractions(instagramId),
    enabled: !!instagramId,
  });
};

export const useGetActiveStories = (instagramId) => {
  return useQuery({
    queryKey: ["activeStories", instagramId],
    queryFn: () => getActiveStories(instagramId),
    enabled: !!instagramId,
  });
};

export const useGetMentionsAndTags = (instagramId) => {
  return useQuery({
    queryKey: ["mentionsAndTags", instagramId],
    queryFn: () => getMentionsAndTags(instagramId),
    enabled: !!instagramId,
  });
};

export const useSearchHashtag = () => {
  return useMutation({
    mutationFn: searchHashtag
  });
};

// Task Category Hooks
export const useGetTaskCategories = (companyId) => {
  return useQuery({
    queryKey: ["taskCategories", companyId],
    queryFn: () => import("./service").then(m => m.getTaskCategories(companyId)),
    enabled: !!companyId,
  });
};

export const useCreateTaskCategory = (companyId, onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => import("./service").then(m => m.createTaskCategory(companyId, data)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["taskCategories", companyId] });
      if (onSuccess) onSuccess();
    },
  });
};

export const useUpdateTaskCategory = (companyId, onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ categoryId, data }) => import("./service").then(m => m.updateTaskCategory(companyId, categoryId, data)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["taskCategories", companyId] });
      if (onSuccess) onSuccess();
    },
  });
};

export const useDeleteTaskCategory = (companyId, onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (categoryId) => import("./service").then(m => m.deleteTaskCategory(companyId, categoryId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["taskCategories", companyId] });
      if (onSuccess) onSuccess();
    },
  });
};

// Department Hooks
export const useGetDepartments = (companyId) => {
  return useQuery({
    queryKey: ["departments", companyId],
    queryFn: () => import("./service").then(m => m.getDepartments(companyId)),
    enabled: !!companyId,
  });
};

export const useCreateDepartment = (companyId, onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => import("./service").then(m => m.createDepartment(companyId, data)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["departments", companyId] });
      queryClient.invalidateQueries({ queryKey: ["departmentDashboard", companyId] });
      if (onSuccess) onSuccess();
    },
  });
};

export const useUpdateDepartment = (companyId, onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ departmentId, data }) => import("./service").then(m => m.updateDepartment(companyId, departmentId, data)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["departments", companyId] });
      queryClient.invalidateQueries({ queryKey: ["departmentDashboard", companyId] });
      if (onSuccess) onSuccess();
    },
  });
};

export const useDeleteDepartment = (companyId, onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (departmentId) => import("./service").then(m => m.deleteDepartment(companyId, departmentId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["departments", companyId] });
      queryClient.invalidateQueries({ queryKey: ["departmentDashboard", companyId] });
      if (onSuccess) onSuccess();
    },
  });
};

export const useGetDepartmentDashboard = (companyId, enabled = true) => {
  return useQuery({
    queryKey: ["departmentDashboard", companyId],
    queryFn: () => import("./service").then((m) => m.getDepartmentDashboard(companyId)),
    enabled: !!companyId && enabled,
    staleTime: 5 * 60 * 1000,
  });
};

export const useIsDepartmentHead = (companyId, enabled = true) => {
  // Uses the lightweight head-status endpoint: the full dashboard payload runs
  // per-employee task aggregations and is far too expensive for the sidebar,
  // header and route guards that only need the flag and member ids.
  const { data, isLoading, isError } = useQuery({
    queryKey: ["departmentHeadStatus", companyId],
    queryFn: () =>
      import("./service").then((m) => m.getDepartmentHeadStatus(companyId)),
    enabled: !!companyId && enabled,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  return {
    isDepartmentHead: !!data?.isDepartmentHead,
    canAccessDepartmentDashboard: !!data?.canAccessDepartmentDashboard,
    departments: data?.departments || [],
    totalActionRequiredCount: data?.totalActionRequiredCount || 0,
    // Intentionally excludes isFetching so a background refresh never swaps a
    // rendered page back to a loading state.
    isLoading,
    isError,
  };
};

export const useGetMeetings = () => {
  return useQuery({
    queryKey: ["meetings"],
    queryFn: () => import("./service").then((m) => m.getMeetings()),
  });
};

export const useGetUpcomingMeetingCount = () => {
  return useQuery({
    queryKey: ["upcomingMeetingCount"],
    queryFn: () => import("./service").then((m) => m.getUpcomingMeetingCount()),
    staleTime: 1000 * 60 * 2,
    refetchInterval: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });
};

export const useCreateMeeting = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => import("./service").then((m) => m.createMeeting(data)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["meetings"] });
      queryClient.invalidateQueries({ queryKey: ["upcomingMeetingCount"] });
    },
  });
};

export const useUpdateMeeting = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ meetingId, data }) =>
      import("./service").then((m) => m.updateMeeting(meetingId, data)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["meetings"] });
      queryClient.invalidateQueries({ queryKey: ["upcomingMeetingCount"] });
    },
  });
};

export const useCancelMeeting = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (meetingId) => import("./service").then((m) => m.cancelMeeting(meetingId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["meetings"] });
      queryClient.invalidateQueries({ queryKey: ["upcomingMeetingCount"] });
    },
  });
};

export const useReportMeetingAttendance = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ meetingId, attendees }) =>
      import("./service").then((m) => m.reportMeetingAttendance(meetingId, attendees)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["meetings"] });
      queryClient.invalidateQueries({ queryKey: ["upcomingMeetingCount"] });
      queryClient.invalidateQueries({ queryKey: ["myPerformance"] });
    },
  });
};

const invalidateMeetings = (queryClient) => {
  queryClient.invalidateQueries({ queryKey: ["meetings"] });
};

export const useAddMeetingActionItem = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ meetingId, data }) =>
      import("./service").then((m) => m.addMeetingActionItem(meetingId, data)),
    onSuccess: () => invalidateMeetings(queryClient),
  });
};

export const useUpdateMeetingActionItem = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ meetingId, itemId, data }) =>
      import("./service").then((m) => m.updateMeetingActionItem(meetingId, itemId, data)),
    onSuccess: () => invalidateMeetings(queryClient),
  });
};

export const useDeleteMeetingActionItem = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ meetingId, itemId }) =>
      import("./service").then((m) => m.deleteMeetingActionItem(meetingId, itemId)),
    onSuccess: () => invalidateMeetings(queryClient),
  });
};

export const useGetGoogleMeetStatus = () => {
  return useQuery({
    queryKey: ["googleMeetStatus"],
    queryFn: () => import("./service").then((m) => m.getGoogleMeetStatus()),
  });
};

export const useConnectGoogleMeet = () => {
  return useMutation({
    mutationFn: (returnTo) =>
      import("./service").then((m) => m.getGoogleMeetConnectUrl(returnTo)),
  });
};

export const useDisconnectGoogleMeet = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => import("./service").then((m) => m.disconnectGoogleMeet()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["googleMeetStatus"] });
    },
  });
};

export const useGenerateMeetingMeetLink = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (meetingId) =>
      import("./service").then((m) => m.generateMeetingMeetLink(meetingId)),
    onSuccess: () => invalidateMeetings(queryClient),
  });
};

export const useUpdateTelegramSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ companyId, enabled, time, botToken, recipients }) =>
      apiClient.put(`/companies/${companyId}/telegram-settings`, { enabled, time, botToken, recipients }).then((res) => res.data),
    onSuccess: (_, { companyId }) => {
      queryClient.invalidateQueries({ queryKey: ["company", companyId] });
    },
  });
};

export const useRegisterTelegramWebhook = () =>
  useMutation({
    mutationFn: ({ companyId }) =>
      apiClient.post(`/telegram/${companyId}/register-webhook`).then((res) => res.data),
  });

export const useRemoveTelegramWebhook = () =>
  useMutation({
    mutationFn: ({ companyId }) =>
      apiClient.post(`/telegram/${companyId}/remove-webhook`).then((res) => res.data),
  });

export const useGetTelegramSubscribers = (companyId) =>
  useQuery({
    queryKey: ["telegram-subscribers", companyId],
    queryFn: () => apiClient.get(`/telegram/${companyId}/subscribers`).then((res) => res.data.subscribers),
    enabled: !!companyId,
  });

export const useRemoveTelegramSubscriber = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ companyId, chatId }) =>
      apiClient.delete(`/telegram/${companyId}/subscribers/${chatId}`).then((res) => res.data),
    onSuccess: (_, { companyId }) => {
      queryClient.invalidateQueries({ queryKey: ["telegram-subscribers", companyId] });
    },
  });
};

// ── Client asset bank ───────────────────────────────────────────────────────
export const useAssetBank = (projectId, params = { status: "available" }, options = {}) => {
  return useQuery({
    queryKey: ["assetBank", projectId, params],
    queryFn: () => import("./service").then((m) => m.getAssetBank(projectId, params)),
    enabled: !!projectId && options.enabled !== false,
    staleTime: 30 * 1000,
  });
};

const useAssetBankMutation = (projectId, mutationFn, onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: ["assetBank", projectId] });
      if (onSuccess) onSuccess(...args);
    },
  });
};

export const useAddAssetBankItems = (projectId, onSuccess) =>
  useAssetBankMutation(
    projectId,
    (data) => import("./service").then((m) => m.addAssetBankItems(projectId, data)),
    onSuccess
  );

export const useUpdateAssetBankItem = (projectId, onSuccess) =>
  useAssetBankMutation(
    projectId,
    ({ itemId, data }) =>
      import("./service").then((m) => m.updateAssetBankItem(projectId, itemId, data)),
    onSuccess
  );

export const useRemoveAssetBankItem = (projectId, onSuccess) =>
  useAssetBankMutation(
    projectId,
    (itemId) => import("./service").then((m) => m.removeAssetBankItem(projectId, itemId)),
    onSuccess
  );

export const useReleaseAssetBankItem = (projectId, onSuccess) =>
  useAssetBankMutation(
    projectId,
    (itemId) => import("./service").then((m) => m.releaseAssetBankItem(projectId, itemId)),
    onSuccess
  );

// ── Shoot sessions ──────────────────────────────────────────────────────────
export const useShootCandidates = (projectId, params = {}, options = {}) => {
  return useQuery({
    queryKey: ["shootCandidates", projectId, params],
    queryFn: () => import("./service").then((m) => m.getShootCandidates(projectId, params)),
    enabled: !!projectId && options.enabled !== false,
    staleTime: 0,
  });
};

export const useGetFacebookLeadForms = (projectId, enabled = true) => {
  return useQuery({
    queryKey: ["facebookLeadForms", projectId],
    queryFn: () =>
      apiClient
        .get("/leads/settings/facebook-forms", { params: { project: projectId } })
        .then((res) => res.data.data),
    enabled: !!projectId && enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
};

export const useImportFacebookLeadForm = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) =>
      apiClient.post("/leads/settings/form-configs/import-facebook", payload).then((res) => res.data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["leadFormConfigs"] });
      await queryClient.invalidateQueries({ queryKey: ["allLeadFormConfigs"] });
    },
  });
};
